import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import L from 'leaflet';
import base from '../styles/base.css?inline';
import { Pentagon, MapPinned, Download, Upload, Move, X, ArrowUp, Trash2, Eye, EyeOff, Image as ImageIcon, BookOpen } from 'lucide';
import { lucideIcon } from '../icon.js';
import {
  NEN5740_STRATEGIES, strategyRule, planFor, makeSamplingPlan, ringAreaSquareMeters, ringLengthMeters,
  PLAN_PARAM_LABELS, PLAN_PARAM_UNITS, convertFromWgs84, convertToWgs84, ringsOverlap, areaOutsideM2,
} from '@mv/models';
import {
  SUBLOCATIONS_EVENT, SUBLOCATION_MAP_CSS, PolygonDrawer, drawSublocations, sublocationBounds,
  nextSublocationName, loadSublocations, saveNewSublocation, saveSublocation, removeSublocation,
} from '../utils/sublocations.js';
import { api } from '../api.js';
import './nen5740-uitleg-dialog.js';
import { EXPORT_FORMATS, collectMonsterplan, exportMonsterplan } from '../utils/monsterplan-export.js';
import { IMPORT_ACCEPT, isDrawingFile, readMonsterplanFile } from '../utils/monsterplan-import.js';
import { RotatedImageOverlay, vectorLayer, layerOpacity, opacityPatch, applyOpacity, plannedPointIcon, baseTiles } from '../utils/kaart-import/leaflet-layers.js';
import { pointInRing } from '../utils/sublocation-geometry.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { renderTekeningenPaneel, TEKENINGEN_PANEEL_CSS } from '../utils/tekeningen-paneel.js';
import { boringIcon, renderKaartLegenda, KAART_LEGENDA_CSS, legendaIngeklapt, bewaarLegendaIngeklapt } from '../utils/kaart-symbolen.js';
import { IMPORT_SOORTEN, MEETSTAAT_KOLOMMEN, MEETSTAAT_KOLOMMEN_NOOT, VOORBEELDEN, downloadVoorbeeld } from '../utils/monsterplan-voorbeeld.js';

/**
 * vooronderzoek-panel — het tabblad Vooronderzoek: een monsterplan volgens NEN 5740.
 *
 * Per deellocatie een tegel met vier stappen (locatie, strategie, boringen, controleren): je werkt
 * één gebied af voordat je aan het volgende begint; een klik op een vlak opent zijn tegel.
 * Overlappen deellocaties, dan komen in de overlap alleen de boringen van de kleinste; bij het
 * intekenen kies je alleen deze of alle overlappende samen.
 *
 * Je tekent de locatie (of deellocaties) op de kaart, kiest per vlak een onderzoeksstrategie,
 * en GeoVoice rekent uit hoeveel boringen en peilbuizen de norm vraagt en tekent ze in: gelijk
 * verdeeld over het vlak, langs het tracé of rond de bron, en genummerd. Je kunt de punten nog
 * verslepen; opslaan maakt er geplande boorpunten van, die op de projectkaart en in de veldapp
 * staan (zie kaart-import, planned_points).
 *
 * De aantallen komen uit NEN 5740 (@mv/models sampling/nen5740.ts). Is de tabel van een
 * strategie nog niet ingevuld, dan vul je de aantallen zelf in; dat staat er dan ook bij.
 *
 * Geplande punten kun je hier ook verplaatsen: verslepen, of aanklikken en de RD-coördinaten
 * intikken.
 *
 * Een PDF of afbeelding (situatietekening, kadasterkaart) gaat naar de uitlijndialoog van de
 * kaartimport (`open-import` detail:{ files }); daarna staat hij hier als achtergrond, net als de
 * andere tekeningen van het project, en teken je de locatie erop.
 *
 * Events: `planned-points-saved` na het opslaan (de pagina laadt de geplande punten opnieuw);
 * `planned-point-update` detail:{ id, data } als je een gepland punt verplaatst of aanpast;
 * `map-layer-update` detail:{ id, data } als je een tekening aan- of uitzet.
 */

const meervoud = (n, een, veel) => `${n} ${n === 1 ? een : veel}`;
const nl = (n) => Math.round(n).toLocaleString('nl-NL');
/** Een plan voor één vlak in de vorm van de invoervelden. */
const leegPlan = () => ({ borings: [{ depthCm: 50, count: 0 }], peilbuizen: 0, peilbuisDepthCm: null, radiusM: 5 });

/**
 * Een deellocatie waarvan de punten blijven staan (handmatig verplaatst), maar die na het
 * vrijmaken van het overlappende deel minder boringen of peilbuizen heeft dan de norm vraagt:
 * vul aan met punten uit het nieuwe plan, telkens het punt dat het verst van alles ligt wat er
 * al staat. Zo blijft het handwerk staan en klopt het aantal weer.
 *
 * @param {Array} kandidaten  punten uit het nieuwe plan buiten het overlappende deel ({ lng, lat, kind })
 * @param {Array} aanwezig    wat er al staat of erbij komt (geplande punten { x, y, kind } of plan { lng, lat, kind })
 * @param {object} inv        de aantallen van de deellocatie (_invoerVan)
 */
function aanvullen(kandidaten, aanwezig, inv) {
  const isPb = (p) => p.kind === 'peilbuis';
  const nodigPb = Number(inv.peilbuizen) || 0;
  const nodigB = (inv.borings || []).reduce((t, b) => t + (Number(b.count) || 0), 0);
  const xy = (p) => [p.lng ?? p.x, p.lat ?? p.y];
  const staat = aanwezig.map(xy);
  const kx = Math.cos(((staat[0]?.[1] ?? 52) * Math.PI) / 180);
  const afstand = (a, b) => Math.hypot((a[0] - b[0]) * kx, a[1] - b[1]);
  const gekozen = [];
  for (const [soort, nodig] of [[true, nodigPb], [false, nodigB]]) {
    let tekort = nodig - aanwezig.filter((p) => isPb(p) === soort).length;
    const pool = kandidaten.filter((p) => isPb(p) === soort);
    while (tekort > 0 && pool.length) {
      let beste = 0, verst = -1;
      pool.forEach((p, i) => {
        const d = staat.length ? Math.min(...staat.map((q) => afstand(xy(p), q))) : Infinity;
        if (d > verst) { verst = d; beste = i; }
      });
      const [p] = pool.splice(beste, 1);
      gekozen.push(p);
      staat.push(xy(p));
      tekort -= 1;
    }
  }
  return gekozen;
}

export class VooronderzoekPanel extends LitElement {
  static properties = {
    projectId: { type: String },
    /** De popup 'Bekijk uitleg' bij stap 2 staat open. */
    _uitlegOpen: { state: true },
    /** Boringen van het project zoals de projectkaart ze krijgt: { guid, name, lat, lng }. */
    borings: { type: Array },
    /** Geplande punten: { id, name, kind, x(lon), y(lat) }. */
    plannedPoints: { type: Array },
    /** Tekeningen van het project (kaartimport): { id, name, kind, visible, georef, style }. */
    mapLayers: { type: Array },
    /** Gepland punt om meteen te openen (vanaf de projectkaart via 'Verplaatsen'). */
    focusPointId: { type: String },
    _subs: { state: true },
    _invoer: { state: true },     // sublocation-id → { borings, peilbuizen, peilbuisDepthCm, radiusM, handmatig }
    _opgaven: { state: true },    // sublocation-id → { gwsCm, excavationCm, suspectTopCm, suspectBottomCm }
    _concept: { state: true },    // { punten: [{ name, kind, lng, lat, depthCm, subId }] } — alle deellocaties samen
    _dlKeuze: { state: true },    // de deellocatie waarvan de tegel openstaat
    _dlStap: { state: true },     // sublocation-id → open stap (1-4); anders de eerste die niet af is
    _dlVelden: { state: true },   // sublocation-id → naam/omschrijving/oppervlakte zoals in stap 1 getypt
    _opgeslagen: { state: true }, // { aantal } na 'Opslaan als geplande boringen', tot er iets verandert
    _importOpen: { state: true }, // de importpopup staat open
    _sleepOver: { state: true },  // er wordt een bestand boven het sleepvlak gehouden
    _importFout: { state: true }, // melding in de popup (verkeerd bestandstype)
    _kolommenOpen: { state: true }, // in de popup: de kolommen van een meetstaat uitgeklapt
    _tekeningenOpen: { state: true }, // paneel 'Tekeningen' op de kaart open
    _bewerken: { state: true },   // verplaatsmodus: geplande punten zijn te verslepen
    _legendaDicht: { state: true }, // legenda ingeklapt
    _base: { state: true },       // ondergrond: 'map' of 'aerial'
    _verplaatst: { state: true }, // id → { x, y }: verschoven in de verplaatsmodus, nog niet bewaard
    _dlMode: { state: true },
    _dlPoints: { state: true },
    _dlName: { state: true },
    _melding: { state: true },
    _opdrachtCodes: { state: true }, // NEN 5740-strategieën die de veldwerkopdracht noemt
    _bezig: { state: true },
    _export: { state: true },     // { gebied, format, system } zolang de exportvraag openstaat
    _puntKeuze: { state: true },  // id van het aangeklikte geplande punt
    _puntForm: { state: true },   // { name, rdX, rdY, depthM } voor dat punt
    _import: { state: true },     // { bestand, vlakken, punten } — gelezen, nog niet toegevoegd
  };

  constructor() {
    super();
    this.borings = [];
    this.plannedPoints = [];
    this.mapLayers = [];
    this._laagObjecten = new Map();
    this._laagLaden = new Set();
    this._subs = [];
    this._invoer = {};
    this._opgaven = {};
    this._concept = null;
    this._bewerken = false;
    this._verplaatst = {};
    this._legendaDicht = legendaIngeklapt();
    this._dlStap = {};
    this._dlVelden = {};
    this._dlKeuze = null;
    this._opgeslagen = null;
    this._dlMode = null;
    this._dlPoints = 0;
    this._dlName = '';
    this._melding = '';
    this._bezig = false;
    this._onSubs = (e) => { if (e.detail?.projectId === this.projectId) this._subs = e.detail.sublocations; };
    this._onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (this._importOpen) this._sluitImportPopup();
      else if (this._export) this._export = null;
      else if (this._dlMode) this._stopTekenen();
      else if (this._bewerken) this._annuleerVerplaatsen();
    };
    this._onResize = () => this._pasHoogteAan();
    // Klik buiten het menu Tekeningen -- ook in de kaart -- sluit het. Exporteren is een modal
    // en sluit via zijn achtergrond: hier meenemen zou hem sluiten bij een klik op een tegel.
    this._onPointerDown = (e) => {
      if (!this._tekeningenOpen) return;
      const pad = e.composedPath();
      const menus = [...(this.renderRoot?.querySelectorAll('.exportmenu') || [])];
      if (menus.some((m) => pad.includes(m))) return;
      this._tekeningenOpen = false;
    };
  }

  /**
   * Kaart en stappen even hoog: van hun bovenkant tot 20 px boven de onderkant van het venster.
   * Zo groeit de kaart niet mee met een lang stappenpaneel; dat scrollt binnen dezelfde hoogte.
   */
  _pasHoogteAan() {
    const vo = this.renderRoot?.querySelector('.vo');
    if (!vo) return;
    const top = vo.getBoundingClientRect().top + window.scrollY;
    const hoogte = Math.max(480, window.innerHeight - top - 20);
    vo.style.setProperty('--vo-hoogte', `${hoogte}px`);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(SUBLOCATIONS_EVENT, this._onSubs);
    window.addEventListener('keydown', this._onKey);
    window.addEventListener('resize', this._onResize);
    document.addEventListener('pointerdown', this._onPointerDown, true);
    // Terug op het monsterplan na het bewerken van de veldwerkopdracht: de codes opnieuw lezen.
    if (this.projectId) this._leesOpdrachtCodes();
  }

  /** De strategieën uit de veldwerkopdracht (concept of goedgekeurd), als voorstel in stap 2. */
  async _leesOpdrachtCodes() {
    const id = this.projectId;
    try {
      const r = await api.getFieldOrder(id);
      if (id === this.projectId) this._opdrachtCodes = r?.fieldOrder?.data?.investigation?.strategyCodes || [];
    } catch (_) {
      if (id === this.projectId) this._opdrachtCodes = [];
    }
  }

  /**
   * Codes uit de opdracht → wat stap 2 kan kiezen. 'VED-HE' zonder vlak/tracé wordt beide
   * varianten; een strategie die de berekening nog niet kent (VEP-OO, ONV-GR, ONB, NUL) komt mee
   * zonder regel, zodat je ziet dat de opdracht hem noemt.
   */
  _strategieVoorstel() {
    const out = [];
    for (const code of this._opdrachtCodes || []) {
      const varianten = strategyRule(code) ? [code] : [`${code}-NL`, `${code}-L`].filter((c) => strategyRule(c));
      if (varianten.length) varianten.forEach((c) => { if (!out.some((x) => x.code === c)) out.push({ code: c, rule: strategyRule(c) }); });
      else if (!out.some((x) => x.code === code)) out.push({ code, rule: null });
    }
    return out;
  }

  disconnectedCallback() {
    window.removeEventListener(SUBLOCATIONS_EVENT, this._onSubs);
    window.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('pointerdown', this._onPointerDown, true);
    this._drawer?.cancel();
    this._ro?.disconnect();
    this._kaart?.remove();
    this._kaart = null;
    super.disconnectedCallback();
  }

  /** Opgaven per project in deze browser; ze horen bij het vooronderzoek, niet bij de deellocatie zelf. */
  get _opgavenKey() { return `mv-monsterplan-opgaven:${this.projectId}`; }

  _leesOpgaven() {
    try { this._opgaven = JSON.parse(localStorage.getItem(this._opgavenKey) || '{}') || {}; } catch (_) { this._opgaven = {}; }
  }

  _zetOpgave(s, sleutel, waarde) {
    // Een diepte wordt in m ingevuld en in cm bewaard; inhoud, aantal en lengte zoals ingevuld.
    const getal = waarde === '' || waarde == null ? null : Number(String(waarde).replace(',', '.'));
    const eenheid = PLAN_PARAM_UNITS[sleutel] || 'depth';
    const v = getal == null || !Number.isFinite(getal) ? null
      : eenheid === 'depth' ? Math.round(getal * 100) : eenheid === 'count' ? Math.max(1, Math.round(getal)) : getal;
    this._opgaven = { ...this._opgaven, [s.id]: { ...(this._opgaven[s.id] || {}), [sleutel]: v } };
    try { localStorage.setItem(this._opgavenKey, JSON.stringify(this._opgaven)); } catch (_) { /* privévenster */ }
    // Nieuwe opgaven: de aantallen en diepten opnieuw uit de norm.
    const { [s.id]: _weg, ...rest } = this._invoer;
    this._invoer = rest;
    this._planGewijzigd();
  }

  firstUpdated() {
    this._pasHoogteAan();
    // Nog een keer zodra de rest van de pagina (kop, tabbladen) zijn plek heeft.
    requestAnimationFrame(() => this._pasHoogteAan());
  }

  updated(changed) {
    if (changed.has('projectId') && this.projectId) { this._leesOpgaven(); this._leesOpdrachtCodes(); }
    if (changed.has('projectId') && this.projectId) {
      loadSublocations(this.projectId).then((l) => { this._subs = l; }).catch((e) => { this._melding = e.message; });
    }
    if (!this._kaart) this._maakKaart();
    if (!this._kaart) return;
    if (['_subs', '_concept', '_dlKeuze'].some((k) => changed.has(k))) this._tekenVlakken();
    if (['borings', 'plannedPoints', '_concept', '_puntKeuze', '_puntForm', '_bewerken', '_verplaatst'].some((k) => changed.has(k))) this._tekenPunten();
    if (changed.has('mapLayers')) this._syncTekeningen();
    // Vanaf de projectkaart: dat punt kiezen en erheen, één keer per verzoek.
    if (this.focusPointId && this.focusPointId !== this._gefocust) {
      const p = (this.plannedPoints || []).find((x) => x.id === this.focusPointId);
      if (p) {
        this._gefocust = this.focusPointId;
        this.dispatchEvent(new CustomEvent('focus-handled', { bubbles: true, composed: true }));
        this._kiesPunt(p);
        this._ingepast = true;
        this._kaart.setView([p.y, p.x], 19);
        this.updateComplete.then(() => this.renderRoot.querySelector('.puntblok')?.scrollIntoView({ block: 'nearest' }));
      }
    }
  }

  // ── Kaart ───────────────────────────────────────────────────────────────

  _maakKaart() {
    const doel = this.renderRoot?.querySelector('#kaart');
    if (!doel) return;
    this._kaart = L.map(doel, { zoomControl: true, attributionControl: true, doubleClickZoom: true });
    // Kaart of luchtfoto, zoals op de projectkaart; de keuze wordt per browser onthouden.
    this._tiles = baseTiles();
    try { this._base = localStorage.getItem('mv-kaart-ondergrond') === 'aerial' ? 'aerial' : 'map'; } catch (_) { this._base = 'map'; }
    for (const l of this._tiles[this._base]) l.addTo(this._kaart);
    this._kaart.attributionControl.setPrefix(false);
    this._vlakLaag = L.layerGroup().addTo(this._kaart);
    this._puntLaag = L.layerGroup().addTo(this._kaart);
    this._conceptLaag = L.layerGroup().addTo(this._kaart);
    this._importLaag = L.layerGroup().addTo(this._kaart);
    this._kaart.createPane('mvDrawings');
    this._kaart.getPane('mvDrawings').style.zIndex = 350;
    this._kaart.setView([52.1, 5.3], 7);
    this._ro = new ResizeObserver(() => this._kaart?.invalidateSize());
    this._ro.observe(doel);
    this._ingepast = false;
    this._tekenVlakken();
    this._tekenPunten();
    this._syncTekeningen();
  }

  _setBase(kind) {
    if (kind === this._base || !this._kaart) return;
    for (const l of this._tiles[this._base]) this._kaart.removeLayer(l);
    for (const l of this._tiles[kind]) l.addTo(this._kaart);
    this._base = kind;
    try { localStorage.setItem('mv-kaart-ondergrond', kind); } catch (_) { /* privévenster */ }
  }

  _tekenVlakken() {
    if (!this._vlakLaag) return;
    drawSublocations(this._vlakLaag, this._subs, {
      // De deellocatie waarvan de tegel openstaat, licht op.
      highlightId: this._dlKeuze,
      onClick: (s) => {
        if (this._dlMode || this._bewerken) return;
        // Klik op een vlak: zijn tegel openen.
        this._openDl(s.id);
      },
    });
    this._pasIn();
  }

  _tekenPunten() {
    if (!this._puntLaag) return;
    this._puntLaag.clearLayers();
    this._conceptLaag.clearLayers();
    for (const b of this.borings || []) {
      if (b.lat == null || b.lng == null) continue;
      // Zelfde symbool als op de projectkaart: donkerblauw, of blauw met een peilbuis.
      L.marker([b.lat, b.lng], { icon: boringIcon(b), interactive: true })
        .bindTooltip(b.name, { direction: 'top', offset: [0, -6] }).addTo(this._puntLaag);
    }
    // Geplande punten. Standaard liggen ze vast (klik = gegevens); alleen in de verplaatsmodus
    // ('Punten verplaatsen') zijn ze te verslepen, en dat wordt pas bij Opslaan in één keer bewaard.
    // Punten die het open voorstel gaat vervangen niet: anders staan er twee sets over elkaar.
    const vervangen = new Set(this._concept?.vervangen || []);
    const sleep = this._bewerken && !this._dlMode;
    for (const p of this.plannedPoints || []) {
      if (p.x == null || p.y == null || vervangen.has(p.id)) continue;
      const gekozen = p.id === this._puntKeuze;
      // Het gekozen punt staat waar het blauwe blok zegt: versleept of ingetikt, nog niet bewaard.
      const nu = this._verplaatst[p.id] || (gekozen && !this._bewerken && this._puntFormPlek()) || p;
      const losSleep = gekozen && !this._bewerken && !this._dlMode;
      const verschoven = !!this._verplaatst[p.id];
      const m = L.marker([nu.y, nu.x], {
        draggable: sleep || losSleep,
        // Het gekozen punt boven de rest, anders valt de markering weg onder de buren.
        zIndexOffset: gekozen ? 1000 : 0,
        title: sleep || losSleep ? `${p.name} — sleep om te verplaatsen` : p.name,
        // Zelfde symbool als op de projectkaart; in de verplaatsmodus met een sleeprand.
        icon: (() => {
          const ic = plannedPointIcon(p, { selected: gekozen || verschoven, showLabel: true });
          return L.divIcon({ ...ic.options, className: `mv-planned${sleep ? ' sleepbaar' : ''}${verschoven ? ' verschoven' : ''}` });
        })(),
      });
      m.on('click', () => { if (!this._bewerken) this._toonPunt(p); });
      m.on('dragend', () => {
        const ll = m.getLatLng();
        if (losSleep) {
          // Alleen in het blauwe blok: bewaren doe je met Opslaan.
          const rd = convertFromWgs84(ll.lng, ll.lat, 'RD');
          if (rd) this._puntForm = { ...this._puntForm, rdX: rd.x.toFixed(2).replace('.', ','), rdY: rd.y.toFixed(2).replace('.', ',') };
          return;
        }
        this._verplaatst = { ...this._verplaatst, [p.id]: { x: ll.lng, y: ll.lat } };
      });
      m.addTo(this._puntLaag);
    }
    // Het concept: nog niet opgeslagen, en te verslepen tot je opslaat. Gestippeld en met een
    // sleepcursor, zodat je ziet dat het nog een voorstel is.
    for (const [i, p] of (this._concept?.punten || []).entries()) {
      const m = L.marker([p.lat, p.lng], {
        draggable: true,
        title: `${p.name} — sleep om te verplaatsen`,
        icon: L.divIcon({
          className: '', iconSize: null,
          html: `<span class="vo-punt ${p.kind}"></span><span class="vo-naam">${p.name}</span>`,
        }),
      });
      m.on('dragend', () => {
        const ll = m.getLatLng();
        const punten = this._concept.punten.map((q, k) => (k === i ? { ...q, lat: ll.lat, lng: ll.lng } : q));
        this._concept = { ...this._concept, punten };
      });
      m.addTo(this._conceptLaag);
    }
    this._pasIn();
  }

  _pasIn() {
    if (this._ingepast || !this._kaart) return;
    const ll = [
      ...this._subs.flatMap((s) => (s.geometry || []).map(([lng, lat]) => [lat, lng])),
      ...(this.borings || []).filter((b) => b.lat != null).map((b) => [b.lat, b.lng]),
      ...(this.plannedPoints || []).filter((p) => p.y != null).map((p) => [p.y, p.x]),
    ];
    if (!ll.length) return;
    this._ingepast = true;
    this._kaart.fitBounds(L.latLngBounds(ll), { padding: [40, 40], maxZoom: 18 });
  }

  _scrollNaar(id) {
    this.renderRoot.querySelector(`[data-sub="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /** De tegel van een deellocatie openen (en de rest dicht) en ernaartoe scrollen. */
  async _openDl(id) {
    this._dlKeuze = id;
    await this.updateComplete;
    this._scrollNaar(id);
  }

  // ── Locatie tekenen ─────────────────────────────────────────────────────

  _startTekenen() {
    if (!this._kaart || this._dlMode) return;
    this._dlMode = 'drawing';
    this._dlPoints = 0;
    this._melding = '';
    if (!this._drawer) {
      this._drawer = new PolygonDrawer(this._kaart, {
        onChange: (n) => { this._dlPoints = n; },
        onFinish: (ring) => this._benoem(ring),
      });
    }
    this._drawer.start();
  }

  async _benoem(ring) {
    this._dlRing = ring;
    this._dlMode = 'naming';
    this._dlName = nextSublocationName(this._subs);
    this._dlVoorbeeld = L.polygon(ring.map(([lng, lat]) => [lat, lng]), { color: '#c2410c', weight: 2, fillOpacity: 0.16, interactive: false })
      .addTo(this._kaart);
    await this.updateComplete;
    const input = this.renderRoot.querySelector('.tekenbalk input');
    input?.focus();
    input?.select();
  }

  async _bewaarVlak() {
    const name = this._dlName.trim();
    if (!name) { this._melding = 'Geef de locatie een naam.'; return; }
    this._bezig = true;
    try {
      // Noemt de veldwerkopdracht precies één strategie die we kunnen berekenen, dan staat die al klaar.
      const voorstel = this._strategieVoorstel().filter((x) => x.rule);
      const s = await saveNewSublocation(this.projectId, {
        name, geometry: this._dlRing, areaM2: Math.round(ringAreaSquareMeters(this._dlRing)),
        ...(voorstel.length === 1 ? { strategyCode: voorstel[0].code } : {}),
      });
      this._stopTekenen();
      this._planGewijzigd();
      // De tegel van de nieuwe deellocatie gaat open bij stap 1: naam, omschrijving en
      // oppervlakte nalopen voordat je een strategie kiest.
      this._dlStap = { ...this._dlStap, [s.id]: 1 };
      this._openDl(s.id);
    } catch (e) {
      this._melding = e.message || 'Opslaan mislukt.';
    } finally {
      this._bezig = false;
    }
  }

  _stopTekenen() {
    this._drawer?.cancel();
    this._dlVoorbeeld?.remove();
    this._dlVoorbeeld = null;
    this._dlRing = null;
    this._dlMode = null;
  }

  // ── Strategie en aantallen ─────────────────────────────────────────────

  /** De maat van een vlak in de eenheid van de strategie. */
  _maat(s, rule) {
    if (!rule) return null;
    if (rule.measure === 'length_m') return ringLengthMeters(s.geometry || []);
    if (rule.measure === 'sources') return 1;
    // Ligt er een kleinere deellocatie in, dan telt alleen het deel erbuiten: die heeft zijn
    // eigen strategie en boringen.
    const binnen = this._kleinereBinnen(s);
    if (binnen.length) return areaOutsideM2(s.geometry, binnen.map((x) => x.geometry));
    return Number(s.areaM2) || ringAreaSquareMeters(s.geometry || []);
  }

  // ── Overlap ─────────────────────────────────────────────────────────────

  _opp(s) { return ringAreaSquareMeters(s.geometry || []); }

  /** De deellocaties die met deze overlappen. */
  _overlappers(s) {
    if ((s.geometry || []).length < 3) return [];
    return this._subs.filter((x) => x.id !== s.id && ringsOverlap(s.geometry, x.geometry || []));
  }

  /**
   * De overlappende deellocaties die kleiner zijn: daar komen geen boringen van deze. Bij gelijke
   * grootte beslist de volgorde, zodat er nooit twee elkaar uitsluiten.
   */
  _kleinereBinnen(s) {
    const i = this._subs.indexOf(s);
    return this._overlappers(s).filter((x) => this._opp(x) < this._opp(s) || (this._opp(x) === this._opp(s) && this._subs.indexOf(x) < i));
  }

  /**
   * Deze deellocatie en de deellocaties die hem RECHTSTREEKS overlappen: die worden samen
   * opnieuw ingetekend. Niet verder via via: overlapt DL01 ook met DL02, dan blijft DL02 zoals hij
   * is (DL01 laat zijn vlak wel vrij, zie _kleinereBinnen).
   */
  _overlapGroep(s) {
    return [s, ...this._overlappers(s)];
  }

  /** De geplande punten van deze deellocatie die iemand met de hand heeft verplaatst. */
  _handmatigVan(s) {
    return this._eerderGepland(s).filter((p) => p.movedAt);
  }

  /**
   * Bij samen intekenen vanuit `focus`: de andere deellocaties waarvan we de punten laten staan,
   * omdat er punten met de hand in verplaatst zijn. Daar wordt alleen het deel opnieuw getekend
   * dat met `focus` overlapt; de rest van dat werk gooien we niet weg.
   */
  _behoudenBij(subs, focus) {
    if (!focus || subs.length < 2) return [];
    return subs.filter((x) => x !== focus && this._handmatigVan(x).length);
  }

  /** Waarom een deellocatie (nog) geen boringen heeft om in te tekenen, of null. */
  _waaromGeenAantallen(s) {
    if (this._totaalVan(s) > 0) return null;
    if (!s.strategyCode) return `${s.name} heeft nog geen strategie.`;
    const rule = strategyRule(s.strategyCode);
    const r = rule ? planFor(rule.code, this._maat(s, rule), this._opgaven[s.id] || {}) : null;
    if (r && !r.ok) return `De norm geeft voor ${s.name} geen aantallen: ${r.reason.charAt(0).toLowerCase()}${r.reason.slice(1)} Vul ze bij ${s.name} zelf in.`;
    return `${s.name} heeft nog geen aantallen.`;
  }

  /** De invoer voor een vlak: wat je zelf invulde, anders wat de norm vraagt, anders leeg. */
  _invoerVan(s) {
    if (this._invoer[s.id]) return this._invoer[s.id];
    const rule = strategyRule(s.strategyCode);
    const r = rule ? planFor(rule.code, this._maat(s, rule), this._opgaven[s.id] || {}) : null;
    if (r?.ok) {
      return {
        borings: r.counts.borings.map((b) => ({ ...b })), peilbuizen: r.counts.peilbuizen,
        peilbuisDepthCm: r.counts.peilbuisDepthCm ?? null, radiusM: 5, handmatig: false,
      };
    }
    return { ...leegPlan(), handmatig: true };
  }

  _zetInvoer(s, patch) {
    this._invoer = { ...this._invoer, [s.id]: { ...this._invoerVan(s), ...patch, handmatig: true } };
    this._planGewijzigd();
  }

  /** Iets aan strategie of aantallen veranderd: ingetekende punten kloppen niet meer. */
  _planGewijzigd() {
    this._concept = null;
    this._opgeslagen = null;
  }

  async _zetStrategie(s, code) {
    this._melding = '';
    // Een andere strategie: de aantallen weer uit de norm halen.
    const { [s.id]: _weg, ...rest } = this._invoer;
    this._invoer = rest;
    this._planGewijzigd();
    try {
      await saveSublocation(this.projectId, s.id, { strategyCode: code || null });
    } catch (e) {
      this._melding = e.message || 'Strategie opslaan mislukt.';
    }
  }

  /**
   * De geplande boringen die het monsterplan eerder in deze deellocatie zette, en die nog niet
   * zijn uitgevoerd. Opnieuw intekenen VERVANGT die: anders komt er een tweede set bovenop.
   * Uitgevoerde punten (er is een boring met die naam) en punten uit een andere bron
   * (kaartimport) blijven staan.
   */
  _eerderGepland(s) {
    const ring = s.geometry || [];
    if (ring.length < 3) return [];
    // Op naam van de deellocatie ('Monsterplan DL01 · VEP'): bij overlap ligt een punt in twee
    // vlakken, maar het hoort bij één. Oude punten met alleen 'Monsterplan' gaan op de ligging.
    const naam = String(s.name);
    const eigen = (note) => note === `Monsterplan ${naam}` || note.startsWith(`Monsterplan ${naam} ·`);
    return (this.plannedPoints || []).filter((p) => {
      if (p.status === 'done' || p.x == null) return false;
      const note = String(p.note || '').trim();
      return eigen(note) || (note === 'Monsterplan' && pointInRing(p.x, p.y, ring));
    });
  }

  /** Alle geplande punten die in het vlak liggen (wat er op de kaart in staat), niet uitgevoerd. */
  _geplandIn(s) {
    const ring = s.geometry || [];
    if (ring.length < 3) return [];
    return (this.plannedPoints || []).filter((p) => p.status !== 'done' && p.x != null && pointInRing(p.x, p.y, ring));
  }

  _totaalVan(s) {
    const inv = this._invoerVan(s);
    return inv.borings.reduce((n, b) => n + (Number(b.count) || 0), 0) + (Number(inv.peilbuizen) || 0);
  }

  /** Opnieuw intekenen terwijl er al geplande boringen staan: eerst vragen. */
  async _vraagEnTekenIn(subs, focus = null) {
    subs = subs.filter((s) => (s.geometry || []).length >= 3 && this._totaalVan(s) > 0);
    if (!subs.length) return;
    const behouden = this._behoudenBij(subs, focus);
    const volledig = subs.filter((s) => !behouden.includes(s));
    const n = volledig.reduce((t, s) => t + this._eerderGepland(s).length, 0);
    if (n || behouden.length) {
      const nieuw = volledig.reduce((t, s) => t + this._totaalVan(s), 0);
      const vervangen = volledig.filter((s) => this._eerderGepland(s).length).map((s) => s.name);
      const regels = [];
      if (n) regels.push(`De huidige ${meervoud(n, 'geplande boring', 'geplande boringen')} in ${vervangen.join(', ')} worden vervangen door ${meervoud(nieuw, 'nieuwe', 'nieuwe')}, ook als je ze hebt verschoven.`);
      for (const x of behouden) {
        regels.push(`In ${x.name} ${this._handmatigVan(x).length === 1 ? 'is 1 boring' : `zijn ${this._handmatigVan(x).length} boringen`} met de hand verplaatst. Die boringen blijven staan; alleen in het deel van ${x.name} dat met ${focus.name} overlapt wordt opnieuw getekend.`);
      }
      regels.push('Andere deellocaties blijven zoals ze zijn. Dit gebeurt meteen.');
      const ok = await confirmDialog({
        title: 'Boringen opnieuw intekenen?',
        message: regels.join('\n\n'),
        confirmLabel: 'Intekenen',
        danger: !!n,
      });
      if (!ok) return;
    }
    await this._intekenen(subs, focus);
  }

  /**
   * Stap 3: de boringen van deze deellocaties intekenen en meteen bewaren als geplande boringen.
   * Nummers lopen door (B01..B12, PB01..PB04) en botsen niet met bestaande of geplande namen.
   *
   * Overlap: van klein naar groot. Een deellocatie krijgt geen boringen in een kleinere die
   * ermee overlapt; die heeft zijn eigen strategie en boringen. Zo voldoen beide aan hun
   * strategie zonder dubbele boringen in het overlappende deel.
   */
  async _intekenen(subs, focus = null) {
    const teIntekenen = [...subs].sort((a, b) => this._opp(a) - this._opp(b));
    // Deellocaties met handmatig verplaatste punten (zie _behoudenBij): daarvan vervangen we
    // alleen de punten in het overlappende deel met `focus`, en tekenen we alleen dáár opnieuw.
    const behouden = new Set(this._behoudenBij(teIntekenen, focus).map((x) => x.id));
    const focusRing = focus?.geometry || [];
    const inOverlap = (lng, lat) => focusRing.length >= 3 && pointInRing(lng, lat, focusRing);
    // Namen die al vergeven zijn -- behalve die van de punten die we vervangen: zo krijgt het
    // nieuwe plan dezelfde nummers en wordt het oude bij het opslaan bijgewerkt in plaats van
    // aangevuld.
    const vervangen = new Set(teIntekenen.flatMap((s) => (behouden.has(s.id)
      ? this._eerderGepland(s).filter((p) => inOverlap(p.x, p.y))
      : this._eerderGepland(s))).map((p) => p.id));
    const blijven = teIntekenen.filter((s) => behouden.has(s.id))
      .map((s) => ({ naam: s.name, aantal: this._eerderGepland(s).filter((p) => !vervangen.has(p.id)).length }));
    const bezet = [...(this.borings || []).map((b) => b.name),
      ...(this.plannedPoints || []).filter((p) => !vervangen.has(p.id)).map((p) => p.name)];
    const punten = [];
    try {
      for (const s of teIntekenen) {
        const rule = strategyRule(s.strategyCode);
        const inv = this._invoerVan(s);
        const ring = s.geometry || [];
        const nieuw = makeSamplingPlan({
          layout: rule?.layout || 'grid',
          counts: {
            borings: inv.borings.filter((b) => b.count > 0),
            peilbuizen: Number(inv.peilbuizen) || 0,
            peilbuisDepthCm: inv.peilbuisDepthCm || null,
          },
          ring,
          center: rule?.layout === 'source' ? [
            ring.reduce((a, p) => a + p[0], 0) / ring.length, ring.reduce((a, p) => a + p[1], 0) / ring.length,
          ] : undefined,
          radiusM: Number(inv.radiusM) || 5,
          exclude: this._kleinereBinnen(s).map((x) => x.geometry),
          existingNames: [...bezet, ...punten.map((p) => p.name)],
        });
        // Behouden deellocatie: van het nieuwe plan alleen wat in het overlappende deel valt.
        // Zo komt daar dezelfde dichtheid als het plan zou geven, en blijft de rest staan.
        let erin = nieuw;
        if (behouden.has(s.id)) {
          erin = nieuw.filter((p) => inOverlap(p.lng, p.lat));
          const blijft = this._eerderGepland(s).filter((p) => !vervangen.has(p.id));
          erin = [...erin, ...aanvullen(nieuw.filter((p) => !inOverlap(p.lng, p.lat)), [...blijft, ...erin], inv)];
        }
        punten.push(...erin.map((p) => ({ ...p, subId: s.id })));
      }
      this._concept = { punten, vervangen: [...vervangen], subIds: teIntekenen.map((s) => s.id), blijven };
      this._opgeslagen = null;
      this._melding = '';
      const ll = punten.map((p) => [p.lat, p.lng]);
      if (this._kaart && ll.length) this._kaart.fitBounds(L.latLngBounds(ll), { padding: [50, 50], maxZoom: 19 });
    } catch (e) {
      this._melding = e.message;
      return;
    }
    await this._bewaarConcept();
  }

  async _bewaarConcept() {
    const c = this._concept;
    if (!c?.punten?.length) return;
    this._bezig = true;
    this._melding = '';
    try {
      const byId = new Map(this._subs.map((s) => [s.id, s]));
      await api.importPlannedPoints(this.projectId, c.punten.map((p) => {
        const s = byId.get(p.subId);
        const strategie = s?.strategyCode ? ` · ${s.strategyCode}` : '';
        return {
          name: p.name, kind: p.kind, x: p.lng, y: p.lat, plannedDepthCm: p.depthCm,
          note: s ? `Monsterplan ${s.name}${strategie}` : 'Monsterplan',
        };
      }), { mode: 'merge', sourceName: 'monsterplan' });
      // Oude punten die in het nieuwe plan niet meer voorkomen (bv. minder boringen) weg; die
      // met een naam uit het nieuwe plan zijn hierboven al bijgewerkt.
      const nieuweNamen = new Set(c.punten.map((p) => p.name.toLowerCase()));
      const oud = (this.plannedPoints || []).filter((p) => (c.vervangen || []).includes(p.id) && !nieuweNamen.has(String(p.name).toLowerCase()));
      for (const p of oud) await api.deletePlannedPoint(p.id);
      this._concept = null;
      this._opgeslagen = { aantal: c.punten.length, vervangen: (c.vervangen || []).length, subIds: c.subIds || [], blijven: c.blijven || [] };
      // Elke ingetekende deellocatie door naar 'Controleren'.
      this._dlStap = { ...this._dlStap, ...Object.fromEntries((c.subIds || []).map((id) => [id, 4])) };
      this.dispatchEvent(new CustomEvent('planned-points-saved', { bubbles: true, composed: true }));
    } catch (e) {
      // Niet half laten staan: het voorstel weg, de melding erbij. De oude punten staan er nog.
      this._concept = null;
      this._melding = `Intekenen mislukt: ${e.message || 'opslaan ging niet goed.'}`;
    } finally {
      this._bezig = false;
    }
  }

  _openExport() {
    this._export = { gebied: '', system: 'RD', fout: '' };
  }

  /** De kaart (of één deellocatie) als bestand: vlakken, geplande punten, het concept en wat al geboord is. */
  _exporteer(format) {
    const { gebied } = this._export;
    // Alleen GeoJSON kent een keuze; de andere formaten hebben hun eigen vaste stelsel.
    const f = EXPORT_FORMATS.find((x) => x.id === format);
    const system = f.systems.length > 1 ? this._export.system : f.systems[0];
    try {
      const verzameling = collectMonsterplan({
        sublocations: this._subs, plannedPoints: this.plannedPoints, concept: this._concept?.punten, borings: this.borings,
        sublocationId: gebied || null,
      });
      if (!verzameling.vlakken.length && !verzameling.punten.length) throw new Error('Er staat niets in dit gebied om te exporteren.');
      const { text, mime, extension } = exportMonsterplan(format, verzameling, {
        naam: verzameling.gebied ? `Monsterplan ${verzameling.gebied}` : 'Monsterplan', system,
      });
      const deel = verzameling.gebied ? `-${verzameling.gebied.replace(/[^\w-]+/g, '_')}` : '';
      const url = URL.createObjectURL(new Blob([text], { type: mime }));
      const a = Object.assign(document.createElement('a'), {
        href: url, download: `monsterplan${deel}-${new Date().toISOString().slice(0, 10)}.${extension}`,
      });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      this._export = null;
      this._melding = verzameling.punten.some((p) => p.status === 'concept')
        ? 'Let op: de export bevat ook de nog niet opgeslagen conceptpunten.' : '';
    } catch (e) {
      // In de modal laten zien: daar kijkt de gebruiker, niet in het paneel ernaast.
      this._export = { ...this._export, fout: e.message || 'Exporteren mislukt.' };
    }
  }

  /**
   * De exportmodal, in dezelfde vorm als de importmodal: bovenin welk gebied (en voor GeoJSON
   * welk stelsel), daaronder de vier formaten als tegels. Een klik op een tegel downloadt meteen.
   * Eerst was dit een uitklapper over de kaart, en die viel half buiten beeld.
   */
  _renderExport() {
    const e = this._export;
    if (!e) return '';
    const zet = (data) => { this._export = { ...e, ...data, fout: '' }; };
    const label = { geojson: ['GEO', 'blauw'], kml: ['KML', 'groen'], dxf: ['DXF', 'oranje'], csv: ['CSV', 'paars'] };
    const stelsel = (x) => (x.systems.length > 1 ? (e.system === 'WGS84' ? 'WGS84' : 'RD') : x.systems[0] === 'RD' ? 'RD' : 'WGS84');
    return html`
      <div class="popup-achtergrond" @click=${() => { this._export = null; }}>
        <div class="popup" role="dialog" aria-modal="true" aria-label="Monsterplan exporteren" @click=${(ev) => ev.stopPropagation()}>
          <header class="popup-kop">
            <div>
              <h3>Monsterplan exporteren</h3>
              <p>Deellocaties, geplande boringen en wat al geboord is, als bestand voor GIS, CAD of de GPS.</p>
            </div>
            <button class="sluit" title="Sluiten" @click=${() => { this._export = null; }}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </header>

          <div class="popup-inhoud">
            <div class="exportkeuzes">
              <label>Gebied
                <select @change=${(ev) => zet({ gebied: ev.target.value })}>
                  <option value="" ?selected=${!e.gebied}>Hele kaart</option>
                  ${this._subs.filter((s) => (s.geometry || []).length >= 3).map((s) => html`
                    <option value=${s.id} ?selected=${e.gebied === s.id}>${s.name}${s.strategyCode ? ` · ${s.strategyCode}` : ''}</option>`)}
                </select>
              </label>
              <label>Coördinaten (GeoJSON)
                <select @change=${(ev) => zet({ system: ev.target.value })}>
                  <option value="RD" ?selected=${e.system !== 'WGS84'}>RD (EPSG:28992)</option>
                  <option value="WGS84" ?selected=${e.system === 'WGS84'}>WGS84 (lengte/breedte)</option>
                </select>
              </label>
            </div>
            ${e.fout ? html`<p class="waarschuwing">${e.fout}</p>` : ''}

            <div class="sectielabel">Kies een formaat</div>
            <div class="formaattegels">
              ${EXPORT_FORMATS.map((x) => html`
                <button class="formaattegel" @click=${() => this._exporteer(x.id)}>
                  <span class="soortlabel ${label[x.id]?.[1] || 'groen'}">${label[x.id]?.[0] || x.label}</span>
                  <span class="tegeltekst">
                    <strong>${x.label}</strong>
                    <span>${x.hint}</span>
                    <code class="ext">.${x.extension} · ${stelsel(x)}</code>
                  </span>
                  <span class="tegeldownload">${unsafeSVG(lucideIcon(Download, 15))}</span>
                </button>`)}
            </div>
          </div>

          <footer class="popup-voet">
            <span class="klein">Het bestand komt in je downloads.</span>
            <button class="knop" @click=${() => { this._export = null; }}>Annuleren</button>
          </footer>
        </div>
      </div>`;
  }

  // ── Tekeningen (achtergrond) ────────────────────────────────────────────

  /** Zelfde lagen als de projectkaart: afbeeldingen uitgelijnd met paspunten, vectoren als lijnen. */
  async _syncTekeningen() {
    if (!this._kaart) return;
    const kaart = this._kaart;
    const gewenst = new Map((this.mapLayers || []).map((l) => [l.id, l]));
    for (const [id, obj] of this._laagObjecten) {
      const l = gewenst.get(id);
      if (!l || !l.visible) obj.remove();
      if (!l) { if (obj._mvUrl) URL.revokeObjectURL(obj._mvUrl); this._laagObjecten.delete(id); }
    }
    for (const l of gewenst.values()) {
      const obj = this._laagObjecten.get(l.id);
      if (obj) {
        if (l.visible && !kaart.hasLayer(obj)) obj.addTo(kaart);
        if (l.kind === 'image' && l.georef) obj.setCorners(l.georef);
        applyOpacity(obj, l, layerOpacity(l));
        continue;
      }
      if (!l.visible || this._laagLaden.has(l.id)) continue;
      this._laagLaden.add(l.id);
      try {
        const blob = await api.fetchMapLayerBlob(l.id);
        let laag;
        if (l.kind === 'image') {
          const url = URL.createObjectURL(blob);
          laag = new RotatedImageOverlay(url, l.georef, { opacity: l.georef?.opacity ?? 0.85, pane: 'mvDrawings' });
          laag._mvUrl = url;
        } else {
          laag = vectorLayer(JSON.parse(await blob.text()), { ...(l.style || {}), opacity: layerOpacity(l) });
        }
        this._laagObjecten.set(l.id, laag);
        if ((this.mapLayers || []).find((x) => x.id === l.id)?.visible && this._kaart === kaart) laag.addTo(kaart);
      } catch (e) {
        console.warn('[vooronderzoek] tekening laden mislukt:', l.name, e.message);
      } finally {
        this._laagLaden.delete(l.id);
      }
    }
  }

  /**
   * Het paneel onder de knop 'Tekeningen' op de kaart: bovenaan altijd 'Tekening uploaden' (de
   * kaartimport, met uitlijnen voor een PDF), daaronder per tekening tonen/verbergen, inzoomen,
   * doorzichtigheid en verwijderen. Zelfde opzet als op de projectkaart.
   */
  _renderTekeningen() {
    const meld = (type, detail) => this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
    return renderTekeningenPaneel({
      lagen: this.mapLayers || [],
      onToggle: (l) => meld('map-layer-update', { id: l.id, data: { visible: !l.visible } }),
      onOpacityInput: (l, o) => applyOpacity(this._laagObjecten.get(l.id), l, o),
      onOpacityChange: (l, o) => meld('map-layer-update', { id: l.id, data: opacityPatch(l, o) }),
      onRealign: (l) => { this._tekeningenOpen = false; meld('map-layer-realign', { layer: l }); },
      onDelete: (l) => meld('map-layer-delete', { id: l.id, name: l.name }),
      onZoom: (l) => this._zoomNaarTekening(l),
      onAdd: () => { this._tekeningenOpen = false; meld('open-import'); },
      onClose: () => { this._tekeningenOpen = false; },
    });
  }

  /** Inzoomen op een tekening (de hoeken van een afbeelding, of de omvang van een CAD/GIS-laag). */
  _zoomNaarTekening(l) {
    const obj = this._laagObjecten.get(l.id);
    let b = null;
    try { b = obj?.getBounds?.(); } catch (_) { b = null; }
    if (!b && l.georef?.topLeft) {
      const g = l.georef;
      b = L.latLngBounds([[g.topLeft[1], g.topLeft[0]], [g.topRight[1], g.topRight[0]], [g.bottomLeft[1], g.bottomLeft[0]]]);
    }
    if (b?.isValid?.()) this._kaart?.fitBounds(b, { padding: [30, 30] });
  }

  // ── Geplande punten verplaatsen ─────────────────────────────────────────

  _kiesPunt(p) {
    const rdp = convertFromWgs84(p.x, p.y, 'RD');
    this._puntKeuze = p.id;
    this._puntForm = {
      name: p.name,
      rdX: rdp ? rdp.x.toFixed(2).replace('.', ',') : '',
      rdY: rdp ? rdp.y.toFixed(2).replace('.', ',') : '',
      depthM: p.plannedDepthCm == null ? '' : String(p.plannedDepthCm / 100).replace('.', ','),
    };
    // Wat er stond: Opslaan kan pas als er iets anders is ingevuld.
    this._puntOrigineel = { ...this._puntForm };
  }

  /** De plek uit het blauwe blok (RD → WGS84), of null als die niet te lezen is. */
  _puntFormPlek() {
    const f = this._puntForm;
    if (!f) return null;
    const getal = (v) => Number(String(v).trim().replace(/\s/g, '').replace(',', '.'));
    const [x, y] = [getal(f.rdX), getal(f.rdY)];
    const w = Number.isFinite(x) && Number.isFinite(y) ? convertToWgs84(x, y, 'RD') : null;
    return w ? { x: w.longitude, y: w.latitude } : null;
  }

  /**
   * Een punt op de kaart aangeklikt: kiezen, en rechts naar boven naar het blauwe blok, dat even
   * oplicht. Het blok staat bovenaan het paneel, dus het paneel gaat naar zijn begin.
   */
  async _toonPunt(p) {
    this._kiesPunt(p);
    await this.updateComplete;
    const blok = this.renderRoot.querySelector('.puntblok');
    if (!blok) return;
    this.renderRoot.querySelector('.paneel')?.scrollTo({ top: 0, behavior: 'smooth' });
    blok.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    blok.classList.remove('flits');
    void blok.offsetWidth;   // de animatie opnieuw laten lopen
    blok.classList.add('flits');
  }

  /** Is er in het blauwe blok iets veranderd ten opzichte van het punt? */
  get _puntGewijzigd() {
    const f = this._puntForm, o = this._puntOrigineel;
    if (!f || !o) return false;
    return Object.keys(o).some((k) => String(f[k] ?? '').trim() !== String(o[k] ?? '').trim());
  }

  // ── Verplaatsmodus ───────────────────────────────────────────────────────

  _startVerplaatsen() {
    if (this._dlMode || this._concept) return;
    this._puntKeuze = null;
    this._tekeningenOpen = false;
    this._verplaatst = {};
    this._bewerken = true;
  }

  _annuleerVerplaatsen() {
    this._verplaatst = {};
    this._bewerken = false;
  }

  /** Alle verschoven punten in één keer bewaren. */
  async _bewaarVerplaatsen() {
    const lijst = Object.entries(this._verplaatst);
    if (!lijst.length) { this._annuleerVerplaatsen(); return; }
    this._bezig = true;
    this._melding = '';
    try {
      for (const [id, xy] of lijst) await api.updatePlannedPoint(id, xy);
      // Meteen de nieuwe plek tonen; de pagina laadt de punten daarna opnieuw.
      // Meteen als handmatig verplaatst markeren (de backend doet dat ook, zie moved_at): zo telt
      // een volgende keer samen intekenen dit werk mee zonder eerst te herladen.
      const nu = new Date().toISOString();
      this.plannedPoints = (this.plannedPoints || []).map((p) => (this._verplaatst[p.id] ? { ...p, ...this._verplaatst[p.id], movedAt: nu } : p));
      this._verplaatst = {};
      this._bewerken = false;
      this._melding = `${meervoud(lijst.length, 'punt', 'punten')} verplaatst en bewaard.`;
      this.dispatchEvent(new CustomEvent('planned-points-saved', { bubbles: true, composed: true }));
    } catch (e) {
      this._melding = `Opslaan mislukt: ${e.message || 'probeer het opnieuw.'} De verschuivingen staan nog klaar.`;
    } finally {
      this._bezig = false;
    }
  }

  _renderVerplaatsbalk() {
    if (!this._bewerken) return '';
    const n = Object.keys(this._verplaatst).length;
    return html`
      <div class="tekenbalk verplaatsbalk">
        ${unsafeSVG(lucideIcon(Move, 15))}
        <span><b>Sleep de punten</b> naar de juiste plek${n ? html` · <b>${n}</b> verplaatst` : ''}.</span>
        <button class="hoofd" ?disabled=${this._bezig} @click=${() => this._bewaarVerplaatsen()}>${this._bezig ? 'Opslaan…' : n ? `Opslaan (${n})` : 'Klaar'}</button>
        <button ?disabled=${this._bezig} @click=${() => this._annuleerVerplaatsen()}>Annuleren</button>
      </div>`;
  }

  _verplaatsPunt(p, data) {
    this.dispatchEvent(new CustomEvent('planned-point-update', { detail: { id: p.id, data }, bubbles: true, composed: true }));
    this._kiesPunt({ ...p, ...data });
  }

  _bewaarPunt() {
    const p = (this.plannedPoints || []).find((x) => x.id === this._puntKeuze);
    const f = this._puntForm;
    if (!p || !f) return;
    const getal = (s) => Number(String(s).trim().replace(/\s/g, '').replace(',', '.'));
    const [x, y] = [getal(f.rdX), getal(f.rdY)];
    const w = Number.isFinite(x) && Number.isFinite(y) ? convertToWgs84(x, y, 'RD') : null;
    if (!w) { this._melding = 'Geen geldige RD-coördinaten.'; return; }
    if (!f.name.trim()) { this._melding = 'Geef het punt een naam.'; return; }
    const diepte = f.depthM === '' ? null : Math.round(getal(f.depthM) * 100);
    this._melding = '';
    this._verplaatsPunt(p, { name: f.name.trim(), x: w.longitude, y: w.latitude, plannedDepthCm: Number.isFinite(diepte) ? diepte : null });
    this._kaart?.panTo([w.latitude, w.longitude]);
  }

  _renderPunt() {
    const p = (this.plannedPoints || []).find((x) => x.id === this._puntKeuze);
    const f = this._puntForm;
    if (!p || !f) return '';
    const veld = (label, key, eenheid = '') => html`
      <label>${label}<span><input inputmode=${key === 'name' ? 'text' : 'decimal'} .value=${f[key]}
        @input=${(e) => { this._puntForm = { ...this._puntForm, [key]: e.target.value }; }}
        @keydown=${(e) => { if (e.key === 'Enter' && this._puntGewijzigd) this._bewaarPunt(); }}>${eenheid}</span></label>`;
    return html`
      <div class="puntblok">
        <div class="kop">
          <div class="punttitel">
            <b>${{ peilbuis: 'Geplande peilbuis', proefgat: 'Gepland proefgat', sleuf: 'Geplande sleuf' }[p.kind] || 'Geplande boring'} ${p.name}</b>
            <span class="klein">${[
              /^Monsterplan\s+(.+)$/.exec(String(p.note || '').trim())?.[1],
              p.plannedDepthCm ? `tot ${String(p.plannedDepthCm / 100).replace('.', ',')} m –mv` : '',
            ].filter(Boolean).join(' · ') || 'Gepland punt'}</span>
          </div>
          <button class="tekst" title="Sluiten" @click=${() => { this._puntKeuze = null; }}>×</button>
        </div>
        <p class="klein">Sleep het punt op de kaart of tik de gegevens in, en klik dan op <b>Opslaan</b>.</p>
        <div class="opgaven">
          ${veld('Naam', 'name')}
          ${veld('Diepte', 'depthM', ' m –mv')}
          ${veld('X (RD)', 'rdX', ' m')}
          ${veld('Y (RD)', 'rdY', ' m')}
        </div>
        <div class="rij"><button class="knop hoofd" ?disabled=${!this._puntGewijzigd} title=${this._puntGewijzigd ? '' : 'Pas eerst iets aan'}
          @click=${() => this._bewaarPunt()}>Opslaan</button></div>
      </div>`;
  }

  /** Bestand lezen en als voorbeeld (oranje, gestippeld) op de kaart zetten; toevoegen is een tweede stap. */
  _openImportPopup() {
    this._importOpen = true;
    this._importFout = '';
    this._sleepOver = false;
  }

  _sluitImportPopup() {
    this._importOpen = false;
    this._sleepOver = false;
  }

  /** Een bestand uit de popup (gekozen of gesleept): controleren op type, dan inlezen. */
  _bestandGekozen(file) {
    if (!file) return;
    const ext = `.${String(file.name).toLowerCase().split('.').pop()}`;
    if (!IMPORT_ACCEPT.split(',').includes(ext)) {
      this._importFout = `${file.name}: dit bestandstype lezen we niet. Kies een van de formaten hieronder.`;
      return;
    }
    this._sluitImportPopup();
    this._leesImport(file);
  }

  async _leesImport(file) {
    if (!file) return;
    this._melding = '';
    if (isDrawingFile(file.name)) {
      this.dispatchEvent(new CustomEvent('open-import', { detail: { files: [file] }, bubbles: true, composed: true }));
      return;
    }
    try {
      const { vlakken, punten } = readMonsterplanFile(file.name, new Uint8Array(await file.arrayBuffer()));
      if (!vlakken.length && !punten.length) throw new Error(`In ${file.name} staan geen vlakken of punten die ik kan gebruiken.`);
      this._import = { bestand: file.name, vlakken, punten };
      this._tekenImport();
    } catch (err) {
      this._import = null;
      this._tekenImport();
      this._melding = `${file.name}: ${err.message || 'kon niet gelezen worden.'}`;
    }
  }

  _tekenImport() {
    if (!this._importLaag) return;
    this._importLaag.clearLayers();
    const imp = this._import;
    if (!imp) return;
    const stijl = { color: '#c2410c', weight: 2, dashArray: '6 4', fillOpacity: 0.08, interactive: false };
    for (const v of imp.vlakken) L.polygon(v.ring.map(([lng, lat]) => [lat, lng]), stijl).addTo(this._importLaag);
    for (const p of imp.punten) {
      L.circleMarker([p.lat, p.lng], { radius: 5, color: '#c2410c', weight: 2, fillColor: '#fff', fillOpacity: 1 })
        .bindTooltip(p.name, { direction: 'top' }).addTo(this._importLaag);
    }
    const alles = [...imp.vlakken.flatMap((v) => v.ring), ...imp.punten.map((p) => [p.lng, p.lat])];
    if (alles.length && this._kaart) {
      this._kaart.fitBounds(L.latLngBounds(alles.map(([lng, lat]) => [lat, lng])), { padding: [40, 40], maxZoom: 19 });
    }
  }

  _sluitImport() {
    this._import = null;
    this._tekenImport();
  }

  /** Vlakken worden deellocaties (een bestaande naam krijgt een volgnummer), punten geplande boorpunten. */
  async _voegImportToe() {
    const imp = this._import;
    if (!imp) return;
    this._bezig = true;
    this._melding = '';
    try {
      const namen = new Set(this._subs.map((s) => s.name.trim().toLowerCase()));
      const vrij = (naam) => {
        let kandidaat = naam;
        for (let i = 2; namen.has(kandidaat.toLowerCase()); i++) kandidaat = `${naam} (${i})`;
        namen.add(kandidaat.toLowerCase());
        return kandidaat;
      };
      for (const v of imp.vlakken) {
        await saveNewSublocation(this.projectId, {
          name: vrij(v.name), geometry: v.ring, areaM2: Math.round(ringAreaSquareMeters(v.ring)),
          ...(v.strategy ? { strategyCode: v.strategy } : {}),
        });
      }
      if (imp.punten.length) {
        await api.importPlannedPoints(this.projectId, imp.punten.map((p) => ({
          name: p.name, kind: p.kind, x: p.lng, y: p.lat, plannedDepthCm: p.depthCm, note: `Import ${imp.bestand}`,
        })), { mode: 'merge', sourceName: imp.bestand });
        this.dispatchEvent(new CustomEvent('planned-points-saved', { bubbles: true, composed: true }));
      }
      this._melding = `${imp.bestand}: ${[
        imp.vlakken.length && meervoud(imp.vlakken.length, 'deellocatie', 'deellocaties'),
        imp.punten.length && meervoud(imp.punten.length, 'gepland punt', 'geplande punten'),
      ].filter(Boolean).join(' en ')} toegevoegd.`;
      this._sluitImport();
    } catch (e) {
      this._melding = e.message || 'Toevoegen mislukt.';
    } finally {
      this._bezig = false;
    }
  }

  _renderImport() {
    const imp = this._import;
    if (!imp) return '';
    const peilbuizen = imp.punten.filter((p) => p.kind === 'peilbuis').length;
    return html`
      <div class="importblok">
        <b>${imp.bestand}</b>
        <p class="klein">${[
          imp.vlakken.length && `${meervoud(imp.vlakken.length, 'vlak', 'vlakken')} (${imp.vlakken.map((v) => v.strategy ? `${v.name} · ${v.strategy}` : v.name).join(', ')})`,
          imp.punten.length && `${meervoud(imp.punten.length, 'punt', 'punten')}${peilbuizen ? `, waarvan ${meervoud(peilbuizen, 'peilbuis', 'peilbuizen')}` : ''}`,
        ].filter(Boolean).join(' en ')}. Ze staan oranje gestippeld op de kaart.</p>
        <p class="klein">Vlakken worden deellocaties, punten geplande boorpunten. Een punt met een naam die al gepland staat wordt bijgewerkt.</p>
        <div class="rij">
          <button class="knop hoofd" ?disabled=${this._bezig} @click=${() => this._voegImportToe()}>${this._bezig ? 'Toevoegen…' : 'Toevoegen'}</button>
          <button class="knop" ?disabled=${this._bezig} @click=${() => this._sluitImport()}>Annuleren</button>
        </div>
      </div>`;
  }

  // ── Weergave ────────────────────────────────────────────────────────────

  render() {
    return html`
      ${this._uitlegOpen ? html`<nen5740-uitleg-dialog start="intro"
        @close=${() => { this._uitlegOpen = false; }}></nen5740-uitleg-dialog>` : ''}
      <div class="vo">
        <div class="kaartvak">
          <div id="kaart" class="kaart"></div>
          <div class="kaartknoppen">
            ${this._dlMode || this._bewerken ? '' : html`
              <button class="knop" ?disabled=${!this._subs.length && !(this.plannedPoints || []).length && !this._concept}
                      @click=${() => this._openExport()}>${unsafeSVG(lucideIcon(Download, 13))} Exporteren</button>
              <button class="knop" @click=${() => this._openImportPopup()}>${unsafeSVG(lucideIcon(Upload, 13))} Importeren</button>
              <div class="exportmenu">
                <button class="knop ${this._tekeningenOpen ? 'aan' : ''}" @click=${() => { this._tekeningenOpen = !this._tekeningenOpen; }}>
                  ${unsafeSVG(lucideIcon(ImageIcon, 13))} Tekeningen${(this.mapLayers || []).length ? html`<span class="telling">${(this.mapLayers || []).length}</span>` : ''}</button>
                ${this._tekeningenOpen ? this._renderTekeningen() : ''}
              </div>
              ${(this.plannedPoints || []).length && !this._bewerken && !this._concept ? html`
                <button class="knop" @click=${() => this._startVerplaatsen()}>${unsafeSVG(lucideIcon(Move, 13))} Punten verplaatsen</button>` : ''}
              <button class="knop hoofd" @click=${() => this._startTekenen()}>${unsafeSVG(lucideIcon(Pentagon, 13))} ${this._subs.length ? 'Extra deellocatie tekenen' : 'Deellocatie tekenen'}</button>`}
            <div class="seg">
              <button class=${this._base !== 'aerial' ? 'on' : ''} @click=${() => this._setBase('map')}>Kaart</button>
              <button class=${this._base === 'aerial' ? 'on' : ''} @click=${() => this._setBase('aerial')}>Luchtfoto</button>
            </div>
          </div>
          ${this._renderTekenbalk()}
          ${this._renderVerplaatsbalk()}
          ${renderKaartLegenda({
            borings: this.borings, plannedPoints: this.plannedPoints, ingeklapt: this._legendaDicht,
            onToggle: () => { this._legendaDicht = !this._legendaDicht; bewaarLegendaIngeklapt(this._legendaDicht); },
          })}
          ${this._concept ? html`<div class="hint sleep">${unsafeSVG(lucideIcon(Move, 14))}
              <span><b>Sleep de punten</b> naar de juiste plek. Ze worden pas bewaard als je opslaat.</span></div>`
            : ''}
        </div>
        <aside class="paneel">
          ${this._renderKop()}
          ${this._melding ? html`<p class="melding">${this._melding}</p>` : ''}
          ${this._renderImport()}
          ${this._renderPunt()}
          ${this._renderTegels()}
        </aside>
        ${this._renderImportPopup()}
        ${this._renderExport()}
      </div>`;
  }

  /**
   * De importpopup: een sleepvlak, welke formaten we lezen en wat er in een meetstaat hoort, en
   * voorbeeldbestanden om dat te laten zien.
   */
  _renderImportPopup() {
    if (!this._importOpen) return '';
    const kies = () => this.renderRoot.querySelector('#importbestand')?.click();
    return html`
      <div class="popup-achtergrond" @click=${() => this._sluitImportPopup()}>
        <div class="popup" role="dialog" aria-modal="true" aria-label="Monsterplan importeren" @click=${(e) => e.stopPropagation()}>
          <header class="popup-kop">
            <div>
              <h3>Monsterplan importeren</h3>
              <p>Neem deellocaties en geplande boringen over uit een bestand van de adviseur of opdrachtgever.</p>
            </div>
            <button class="sluit" title="Sluiten" @click=${() => this._sluitImportPopup()}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </header>

          <div class="popup-inhoud">
            <div class="sleepvlak ${this._sleepOver ? 'erboven' : ''}" role="button" tabindex="0"
                 @click=${kies} @keydown=${(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kies(); } }}
                 @dragenter=${(e) => { e.preventDefault(); this._sleepOver = true; }}
                 @dragover=${(e) => { e.preventDefault(); this._sleepOver = true; }}
                 @dragleave=${(e) => { if (!e.currentTarget.contains(e.relatedTarget)) this._sleepOver = false; }}
                 @drop=${(e) => { e.preventDefault(); this._sleepOver = false; this._bestandGekozen(e.dataTransfer?.files?.[0]); }}>
              <span class="pijl">${unsafeSVG(lucideIcon(ArrowUp, 16))}</span>
              <strong>Sleep een bestand hierheen</strong>
              <span class="klein">of <span class="link">kies een bestand</span> · we herkennen het type zelf</span>
              <input id="importbestand" type="file" hidden accept=${IMPORT_ACCEPT}
                     @click=${(e) => e.stopPropagation()}
                     @change=${(e) => { const f = e.target.files?.[0]; e.target.value = ''; this._bestandGekozen(f); }}>
            </div>
            ${this._importFout ? html`<p class="waarschuwing">${this._importFout}</p>` : ''}

            <div class="sectielabel">Wat kun je importeren</div>
            <div class="soorten">
              ${IMPORT_SOORTEN.map((t) => html`
                <div class="soort">
                  <span class="soortlabel ${t.kleur}">${t.label}</span>
                  <div class="soorttekst">
                    <strong>${t.titel}</strong>
                    <span>${t.uitleg}</span>
                    <code class="ext">${t.ext.join(' ')}</code>
                    ${t.kolommen ? html`
                      <button class="link kolomknop" @click=${() => { this._kolommenOpen = !this._kolommenOpen; }}>
                        ${this._kolommenOpen ? 'Kolommen verbergen' : 'Welke kolommen?'}</button>` : ''}
                  </div>
                  <span class="wordt">→ ${t.wordt}</span>
                  ${t.kolommen && this._kolommenOpen ? html`
                    <div class="kolomtabel">
                      ${MEETSTAAT_KOLOMMEN.map((k) => html`
                        <div class="kolomrij"><code>${k.kolom}</code><span>${k.uitleg}</span><code class="vb">${k.voorbeeld}</code></div>`)}
                      <div class="kolomnoot">${MEETSTAAT_KOLOMMEN_NOOT}</div>
                    </div>` : ''}
                </div>`)}
            </div>
          </div>

          <footer class="popup-voet">
            <span class="klein">Voorbeeld downloaden:
              ${VOORBEELDEN.map((v, i) => html`${i ? ' · ' : ''}<button class="link" @click=${() => downloadVoorbeeld(v)}>${v.label}</button>`)}
            </span>
            <button class="knop" @click=${() => this._sluitImportPopup()}>Annuleren</button>
          </footer>
        </div>
      </div>`;
  }

  _renderTekenbalk() {
    if (this._dlMode === 'drawing') {
      return html`
        <div class="tekenbalk">
          <span>${this._dlPoints < 3 ? html`Klik de hoekpunten aan${this._dlPoints ? html` (<b>${this._dlPoints}</b> gezet)` : ''}.`
            : html`<b>${this._dlPoints}</b> punten · klik op het eerste punt of dubbelklik om af te sluiten.`}</span>
          <button ?disabled=${!this._dlPoints} @click=${() => this._drawer.undo()}>Punt terug</button>
          <button class="hoofd" ?disabled=${this._dlPoints < 3} @click=${() => this._drawer.finish()}>Afsluiten</button>
          <button @click=${() => this._stopTekenen()}>Annuleren</button>
        </div>`;
    }
    if (this._dlMode === 'naming') {
      return html`
        <div class="tekenbalk">
          <span>Naam:</span>
          <input .value=${this._dlName} maxlength="60" @input=${(e) => { this._dlName = e.target.value; }}
                 @keydown=${(e) => { if (e.key === 'Enter') this._bewaarVlak(); }}>
          <button class="hoofd" ?disabled=${this._bezig} @click=${() => this._bewaarVlak()}>${this._bezig ? 'Opslaan…' : 'Opslaan'}</button>
          <button ?disabled=${this._bezig} @click=${() => this._stopTekenen()}>Annuleren</button>
        </div>`;
    }
    return '';
  }

  // ── Tegels per deellocatie ──────────────────────────────────────────────
  //
  // Elke deellocatie is een tegel met zijn eigen vier stappen: locatie, strategie, boringen,
  // controleren. Zo werk je één gebied af voordat je aan het volgende begint; een klik op een
  // vlak op de kaart opent zijn tegel.

  /** Welke stappen van een deellocatie af zijn. */
  _klaarVan(s) {
    const gepland = this._eerderGepland(s).length > 0;
    return { 1: (s.geometry || []).length >= 3, 2: !!s.strategyCode, 3: gepland, 4: gepland };
  }

  /** De open stap van een deellocatie: wat je koos, anders de eerste die nog niet af is. */
  _stapVan(s) {
    if (this._dlStap[s.id]) return this._dlStap[s.id];
    const k = this._klaarVan(s);
    return !k[1] ? 1 : !k[2] ? 2 : !k[3] ? 3 : 4;
  }

  _bereikbaarVan(s, n) {
    if (n === 1) return true;
    if (n === 4) return this._eerderGepland(s).length > 0;
    return this._klaarVan(s)[1];
  }

  _naarStapVan(s, n) {
    if (this._bereikbaarVan(s, n)) this._dlStap = { ...this._dlStap, [s.id]: n };
  }

  /** Hoeveel geplande boringen het monsterplan in de deellocaties heeft staan (opgeslagen). */
  get _aantalGepland() {
    return this._subs.reduce((n, s) => n + this._eerderGepland(s).length, 0);
  }

  _renderKop() {
    const subs = this._subs;
    const af = subs.filter((s) => this._klaarVan(s)[3]).length;
    return html`
      <div class="voortgang">
        <div class="voortgang-kop"><h3>Monsterplan (NEN 5740)</h3>
          <span>${subs.length ? `${af} van ${meervoud(subs.length, 'deellocatie', 'deellocaties')} ingetekend` : ''}</span></div>
        ${subs.length ? html`<div class="balk"><div style="width:${(af / subs.length) * 100}%"></div></div>` : ''}
      </div>`;
  }

  _renderTegels() {
    const subs = this._subs;
    if (!subs.length) {
      return html`
        <div class="leeg">${unsafeSVG(lucideIcon(MapPinned, 22))}
          <span>Nog geen deellocatie. Klik <b>Deellocatie tekenen</b> en zet de hoekpunten op de kaart. Een tekening of
            meetstaat kun je ook <b>importeren</b>.</span></div>
        <div class="acties"><button class="knop hoofd" ?disabled=${!!this._dlMode} @click=${() => this._startTekenen()}>
          ${unsafeSVG(lucideIcon(Pentagon, 14))} Deellocatie tekenen</button></div>`;
    }
    return html`
      <div class="tegels">${subs.map((s) => this._renderTegel(s))}</div>
      <button class="nieuwdl" ?disabled=${!!this._dlMode} @click=${() => this._startTekenen()}>
        ${unsafeSVG(lucideIcon(Pentagon, 15))} Extra deellocatie tekenen</button>`;
  }

  _renderTegel(s) {
    const open = this._dlKeuze === s.id;
    const k = this._klaarVan(s);
    const af = [1, 2, 3, 4].filter((n) => k[n]).length;
    const opp = this._opp(s);
    const inVlak = this._geplandIn(s).length;
    const overlap = this._overlappers(s);
    return html`
      <section class="tegel ${open ? 'open' : ''}" data-sub=${s.id}>
        <header @click=${() => { this._dlKeuze = open ? null : s.id; }}>
          <span class="dl-ico"></span>
          <div class="tegelkop">
            <div><strong class="dl">${s.name}</strong>${s.strategyCode ? html`<span class="code">${s.strategyCode}</span>` : ''}
              ${overlap.length ? html`<span class="overlapchip" title=${`Overlapt met ${overlap.map((x) => x.name).join(', ')}`}>overlap</span>` : ''}</div>
            <span class="klein">${opp ? `${nl(opp)} m²` : 'geen vlak'} · ${meervoud(inVlak, 'geplande boring', 'geplande boringen')}</span>
          </div>
          <span class="tegelstand ${af === 4 ? 'af' : ''}" title="Stappen af">${af === 4 ? '✓' : `${af}/4`}</span>
          <span class="tegelpijl">${open ? '▾' : '▸'}</span>
        </header>
        ${open ? html`
          <div class="tegelinhoud">
            ${overlap.length ? this._renderOverlapMelding(s, overlap) : ''}
            ${[1, 2, 3, 4].map((n) => this._renderDlStap(s, n))}
          </div>` : ''}
      </section>`;
  }

  /** Overlappende deellocaties: wat dat betekent voor de boringen. */
  _renderOverlapMelding(s, overlap) {
    const kleiner = this._kleinereBinnen(s);
    return html`
      <div class="overlapmelding">
        <b>${s.name} overlapt met ${overlap.map((x) => x.name).join(', ')}.</b>
        In het overlappende deel komen alleen de boringen van de kleinste deellocatie.
        ${kleiner.length ? html`${s.name} rekent daarom met de oppervlakte buiten ${kleiner.map((x) => x.name).join(', ')}.` : ''}
        Bij het intekenen kies je of alleen ${s.name} wordt berekend, of ${[s, ...overlap].map((x) => x.name).join(' en ')} samen.
      </div>`;
  }

  _renderDlStap(s, n) {
    const titels = {
      1: ['Locatie', 'Het vlak op de kaart'],
      2: ['Strategie', 'Onderzoeksstrategie volgens NEN 5740'],
      3: ['Boringen', 'Aantallen en diepte, daarna intekenen'],
      4: ['Controleren', 'Controleer of de punten goed staan'],
    };
    const open = this._stapVan(s) === n;
    const af = this._klaarVan(s)[n] && !open;
    const bereikbaar = this._bereikbaarVan(s, n);
    const [titel, uitleg] = titels[n];
    const inhoud = { 1: this._renderLocatie, 2: this._renderStrategie, 3: this._renderBoringen, 4: this._renderControle }[n];
    return html`
      <section class="stap ${open ? 'open' : ''} ${af ? 'af' : ''} ${bereikbaar ? '' : 'dicht'}">
        <header @click=${() => { if (!open) this._naarStapVan(s, n); }}>
          <span class="nummer">${af ? '✓' : n}</span>
          <div class="stapkop">
            <strong>${titel}</strong>
            <span>${this._stapSamenvattingVan(s, n) || uitleg}</span>
          </div>
          ${af && bereikbaar ? html`<button class="tekst groen" @click=${(e) => { e.stopPropagation(); this._naarStapVan(s, n); }}>Wijzig</button>` : ''}
          <!-- Welke strategie past? De uitleg en de tabellen uit NEN 5740, naast de keuze. -->
          ${n === 2 && open ? html`
            <button class="uitlegtegel" @click=${(e) => { e.stopPropagation(); this._uitlegOpen = true; }}>
              ${unsafeSVG(lucideIcon(BookOpen, 15))} Bekijk uitleg
            </button>` : ''}
        </header>
        ${open ? html`<div class="stapinhoud">${inhoud.call(this, s)}</div>` : ''}
      </section>`;
  }

  _stapSamenvattingVan(s, n) {
    if (n === 1 && (s.geometry || []).length >= 3) {
      const omtrek = ringLengthMeters(s.geometry || []);
      return `${nl(this._opp(s))} m² · ${nl(omtrek)} m omtrek`;
    }
    if (n === 2 && s.strategyCode) return `${s.strategyCode}${strategyRule(s.strategyCode) ? ` — ${strategyRule(s.strategyCode).label}` : ''}`;
    const gepland = this._eerderGepland(s).length;
    if (n === 3 && gepland) return `${meervoud(gepland, 'geplande boring', 'geplande boringen')} ingetekend`;
    return '';
  }

  // Stap 1 — het vlak: naam, omschrijving en oppervlakte. De oppervlakte komt uit het getekende
  // vlak maar mag met de hand anders (een kadastrale maat, of zonder de bebouwing).

  /** Wat in stap 1 staat: wat je typte, anders wat opgeslagen is. */
  _veldenVan(s) {
    return this._dlVelden[s.id] || {
      name: s.name || '', description: s.description || '',
      areaM2: s.areaM2 != null ? String(Math.round(Number(s.areaM2))) : String(Math.round(this._opp(s))),
    };
  }

  _zetVeld(s, key, value) {
    this._dlVelden = { ...this._dlVelden, [s.id]: { ...this._veldenVan(s), [key]: value } };
  }

  /** De wijzigingen van stap 1 ten opzichte van wat opgeslagen is, of null als er niets is. */
  _veldWijzigingen(s) {
    const v = this._dlVelden[s.id];
    if (!v) return null;
    const area = Math.round(Number(String(v.areaM2).replace(',', '.')));
    const out = {};
    if (v.name.trim() && v.name.trim() !== s.name) out.name = v.name.trim();
    if (v.description.trim() !== (s.description || '')) out.description = v.description.trim() || null;
    if (Number.isFinite(area) && area > 0 && area !== Math.round(Number(s.areaM2 ?? this._opp(s)))) out.areaM2 = area;
    return Object.keys(out).length ? out : null;
  }

  async _bewaarVelden(s) {
    const v = this._dlVelden[s.id];
    if (v && !v.name.trim()) { this._melding = 'Geef de deellocatie een naam.'; return false; }
    const data = this._veldWijzigingen(s);
    if (!data) return true;
    this._bezig = true;
    this._melding = '';
    try {
      await saveSublocation(this.projectId, s.id, data);
      const { [s.id]: _weg, ...rest } = this._dlVelden;
      this._dlVelden = rest;
      // Een andere oppervlakte geeft andere aantallen.
      if (data.areaM2 != null) this._planGewijzigd();
      return true;
    } catch (e) {
      this._melding = e.message || 'Opslaan mislukt.';
      return false;
    } finally {
      this._bezig = false;
    }
  }

  _renderLocatie(s) {
    const kleiner = this._kleinereBinnen(s);
    const v = this._veldenVan(s);
    const berekend = Math.round(this._opp(s));
    const gewijzigd = !!this._veldWijzigingen(s);
    // Een paar m² verschil is afronding van eerder (het vlak is opnieuw berekend), geen handwerk.
    const getypt = Math.round(Number(String(v.areaM2).replace(',', '.')));
    const handmatig = (s.geometry || []).length >= 3 && Math.abs(getypt - berekend) > Math.max(2, berekend * 0.001);
    return html`
      <div class="dlvelden">
        <label class="veld">Naam
          <input .value=${v.name} maxlength="60" @input=${(e) => this._zetVeld(s, 'name', e.target.value)}></label>
        <label class="veld"><span>Omschrijving <span class="optie">· optioneel</span></span>
          <input .value=${v.description} maxlength="200" placeholder="Bijv. voormalige tankplaats"
                 @input=${(e) => this._zetVeld(s, 'description', e.target.value)}></label>
        <label class="veld">Oppervlakte (m²)
          <input type="number" min="1" step="1" inputmode="numeric" .value=${v.areaM2}
                 @input=${(e) => this._zetVeld(s, 'areaM2', e.target.value)}>
          ${(s.geometry || []).length >= 3 ? html`<span class="klein">${handmatig
            ? html`Met de hand aangepast; berekend uit het vlak: ${nl(berekend)} m².
                <button class="tekst" @click=${() => this._zetVeld(s, 'areaM2', String(berekend))}>Terug naar berekend</button>`
            : 'Berekend uit het getekende vlak.'}</span>` : html`<span class="klein">Geen vlak getekend.</span>`}
        </label>
      </div>
      ${kleiner.length ? html`<p class="klein">Netto ${nl(areaOutsideM2(s.geometry, kleiner.map((x) => x.geometry)))} m² buiten
        ${kleiner.map((x) => x.name).join(', ')}.</p>` : ''}
      <div class="acties">
        <button class="tekst" style="margin-right:auto" ?disabled=${this._bezig} @click=${() => this._verwijderDl(s)}>Verwijder deellocatie</button>
        <button class="knop" ?disabled=${this._bezig || !gewijzigd} @click=${() => this._bewaarVelden(s)}>Opslaan</button>
        <button class="knop hoofd" ?disabled=${this._bezig}
                @click=${async () => { if (await this._bewaarVelden(s)) this._naarStapVan(s, 2); }}>Volgende: strategie</button>
      </div>`;
  }

  async _verwijderDl(s) {
    const ok = await confirmDialog({
      title: `Deellocatie ${s.name} verwijderen?`,
      message: 'De boringen en geplande punten blijven staan.',
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    this._bezig = true;
    try {
      await removeSublocation(this.projectId, s.id);
      if (this._dlKeuze === s.id) this._dlKeuze = null;
      this._planGewijzigd();
    } catch (e) {
      this._melding = e.message || 'Verwijderen mislukt.';
    } finally {
      this._bezig = false;
    }
  }

  // Stap 2 — de strategie, met de opgaven die de norm daarbij vraagt.
  _renderStrategie(s) {
    const rule = strategyRule(s.strategyCode);
    const maat = this._maat(s, rule);
    const r = rule ? planFor(rule.code, maat, this._opgaven[s.id] || {}) : null;
    const opg = this._opgaven[s.id] || {};
    const m = (cm) => (cm == null ? '' : String(cm / 100).replace('.', ','));
    const kleiner = this._kleinereBinnen(s);
    return html`
      ${this._strategieVoorstel().length ? html`
        <div class="opdracht">
          <span class="klein">Uit de veldwerkopdracht:</span>
          ${this._strategieVoorstel().map((x) => html`
            <button class="dlchip ${s.strategyCode === x.code ? 'aan' : ''}" ?disabled=${!x.rule}
              title=${x.rule ? x.rule.label : 'Deze strategie zit nog niet in de berekening: kies de aantallen in stap 3 zelf.'}
              @click=${() => { if (s.strategyCode !== x.code) this._zetStrategie(s, x.code); }}>${x.code}</button>`)}
        </div>` : ''}
      <label class="veld">Strategie
        <select @change=${(e) => this._zetStrategie(s, e.target.value)}>
          <option value="" ?selected=${!s.strategyCode}>Kies een strategie…</option>
          ${NEN5740_STRATEGIES.map((st) => html`
            <option value=${st.code} ?selected=${s.strategyCode === st.code}>${st.code} — ${st.label}</option>`)}
        </select>
      </label>
      ${rule ? html`<p class="klein">${rule.description}</p>` : ''}
      ${rule?.measure === 'area_m2' && kleiner.length ? html`<p class="klein">Gerekend met ${nl(maat)} m²: de oppervlakte buiten
        ${kleiner.map((x) => x.name).join(', ')}.</p>` : ''}
      ${rule?.measure === 'length_m' && maat ? html`<p class="klein">Tracé van ${nl(maat)} m.</p>` : ''}
      ${rule ? html`
        <div class="opgaven">
          ${rule.params.map((k) => {
            const eenheid = PLAN_PARAM_UNITS[k] || 'depth';
            const waarde = eenheid === 'depth' ? m(opg[k]) : opg[k] == null ? '' : String(opg[k]).replace('.', ',');
            return html`
            <label>${PLAN_PARAM_LABELS[k]}
              <span><input inputmode="decimal" placeholder=${eenheid === 'count' ? '1' : 'onbekend'} .value=${waarde}
                     @change=${(e) => this._zetOpgave(s, k, e.target.value)}> ${({ depth: 'm –mv', m3: 'm³', count: 'stuks', m: 'm' })[eenheid]}</span>
            </label>`;
          })}
        </div>` : ''}
      ${r && !r.ok ? html`<p class="waarschuwing">${r.reason} Vul de aantallen in stap 3 zelf in.</p>` : ''}
      ${r?.ok ? html`
        <div class="normblok">
          <p class="norm">${rule.source}: ${meervoud(r.totalBorings, 'boring', 'boringen')}, waarvan ${meervoud(r.counts.peilbuizen, 'peilbuis', 'peilbuizen')}.</p>
          <p class="klein">Te analyseren: ${[...(r.counts.soilAnalyses || []).map((a) => `${a.count} ${a.label}`),
            r.counts.waterAnalyses ? `${r.counts.waterAnalyses} grondwater` : ''].filter(Boolean).join(' · ')}.</p>
          ${(r.counts.notes || []).map((n) => html`<p class="notitie">${n}</p>`)}
        </div>` : ''}
      <div class="acties"><button class="knop hoofd" ?disabled=${!s.strategyCode} @click=${() => this._naarStapVan(s, 3)}>Volgende: boringen</button></div>`;
  }

  // Stap 3 — aantallen en diepte, dan intekenen: alleen deze, of samen met de overlappende.
  _renderBoringen(s) {
    const geenVlak = (s.geometry || []).length < 3;
    const groep = this._overlapGroep(s);
    const eigen = this._totaalVan(s);
    const vervangt = this._eerderGepland(s).length > 0;
    const knop = (label, subs, hoofd = true) => html`
      <button class="knop ${hoofd ? 'hoofd' : ''}" ?disabled=${this._bezig || !subs.some((x) => this._totaalVan(x) > 0)}
              @click=${() => this._vraagEnTekenIn(subs, s)}>${this._bezig ? 'Intekenen…' : label}</button>`;
    let acties;
    if (groep.length > 1) {
      const anderen = groep.filter((x) => x !== s);
      const zonder = anderen.filter((x) => this._waaromGeenAantallen(x));
      const groter = anderen.filter((x) => !this._kleinereBinnen(s).includes(x) && this._eerderGepland(x).length);
      const namen = groep.map((x) => x.name).join(' en ');
      acties = html`
        <div class="overlapkeuze">
          <b>${s.name} overlapt met ${anderen.map((x) => x.name).join(', ')}.</b>
          <span>Bereken je alleen ${s.name}, of ${namen} samen? Samen houdt GeoVoice met beide strategieën tegelijk rekening,
            zonder dubbele boringen in het overlappende deel.</span>
          ${groter.length ? html`<span class="klein">Alleen ${s.name}: de geplande boringen van ${groter.map((x) => x.name).join(', ')} die in
            ${s.name} liggen, blijven dan staan.</span>` : ''}
          ${anderen.filter((x) => this._handmatigVan(x).length).map((x) => html`<span class="klein">In ${x.name}
            ${this._handmatigVan(x).length === 1 ? 'is 1 boring' : `zijn ${this._handmatigVan(x).length} boringen`} met de hand verplaatst.
            Samen tekent GeoVoice daarom alleen ${s.name} en het overlappende deel opnieuw; de rest van ${x.name} blijft staan.</span>`)}
          ${zonder.map((x) => html`
            <span class="klein">Samen kan nog niet. ${this._waaromGeenAantallen(x)}
              <button class="tekst" @click=${() => { this._naarStapVan(x, this._klaarVan(x)[2] ? 3 : 2); this._openDl(x.id); }}>Naar ${x.name}</button></span>`)}
          <div class="acties">
            ${knop(`Alleen ${s.name} (${eigen})`, [s], !!zonder.length)}
            ${zonder.length ? '' : knop(`${namen} samen (${groep.map((x) => (x !== s && this._handmatigVan(x).length
              ? `${x.name} alleen overlap` : `${x.name} ${this._totaalVan(x)}`)).join(' + ')})`, groep)}
          </div>
        </div>`;
    } else {
      acties = html`<div class="acties">${knop(`${vervangt ? 'Opnieuw intekenen' : 'Boringen intekenen'} (${eigen})`, [s])}</div>`;
    }
    return html`
      <p class="klein">Aantallen komen uit het NEN-advies; pas aan waar nodig.</p>
      ${vervangt ? html`<p class="klein">Heeft al ${meervoud(this._eerderGepland(s).length, 'geplande boring', 'geplande boringen')}; intekenen <b>vervangt</b> die.</p>` : ''}
      ${geenVlak ? html`<p class="waarschuwing">Deze deellocatie heeft geen vlak op de kaart; er wordt niets ingetekend.</p>` : this._renderAantallen(s)}
      ${geenVlak ? '' : acties}`;
  }

  _renderAantallen(s) {
    const rule = strategyRule(s.strategyCode);
    const inv = this._invoerVan(s);
    const zetBoring = (i, patch) => this._zetInvoer(s, { borings: inv.borings.map((b, k) => (k === i ? { ...b, ...patch } : b)) });
    return html`
      <div class="aantallen">
        ${inv.borings.map((b, i) => html`
          <div class="rij">
            <input type="number" min="0" .value=${String(b.count)} @change=${(e) => zetBoring(i, { count: Math.max(0, Math.round(Number(e.target.value) || 0)) })}>
            <span title=${b.label || ''}>boringen tot</span>
            <input type="number" min="10" step="10" .value=${String(b.depthCm)} @change=${(e) => zetBoring(i, { depthCm: Math.max(10, Math.round(Number(e.target.value) || 0)) })}>
            <span>cm –mv</span>
            ${inv.borings.length > 1 ? html`<button class="tekst" title="Regel weghalen"
              @click=${() => this._zetInvoer(s, { borings: inv.borings.filter((_, k) => k !== i) })}>×</button>` : ''}
          </div>`)}
        <div class="rij">
          <input type="number" min="0" .value=${String(inv.peilbuizen)} @change=${(e) => this._zetInvoer(s, { peilbuizen: Math.max(0, Math.round(Number(e.target.value) || 0)) })}>
          <span>peilbuizen tot</span>
          <input type="number" min="10" step="10" placeholder="—" .value=${inv.peilbuisDepthCm == null ? '' : String(inv.peilbuisDepthCm)}
                 @change=${(e) => this._zetInvoer(s, { peilbuisDepthCm: e.target.value === '' ? null : Math.round(Number(e.target.value)) })}>
          <span>cm –mv</span>
        </div>
        ${rule?.layout === 'source' ? html`
          <div class="rij"><span>straal rond de bron</span>
            <input type="number" min="1" .value=${String(inv.radiusM)} @change=${(e) => this._zetInvoer(s, { radiusM: Math.max(1, Number(e.target.value) || 5) })}>
            <span>m</span></div>` : ''}
        <div class="rij">
          <button class="tekst groen" @click=${() => this._zetInvoer(s, { borings: [...inv.borings, { depthCm: 200, count: 0 }] })}>+ diepte</button>
          ${inv.handmatig && rule ? html`<button class="tekst" @click=${() => { const { [s.id]: _w, ...rest } = this._invoer; this._invoer = rest; this._planGewijzigd(); }}>Terug naar de norm</button>` : ''}
        </div>
      </div>`;
  }

  // Stap 4 — controleren: de geplande boringen van deze deellocatie; verslepen wordt in één keer bewaard.
  _renderControle(s) {
    const eigen = this._eerderGepland(s);
    const boringen = eigen.filter((p) => p.kind !== 'peilbuis');
    const pb = eigen.filter((p) => p.kind === 'peilbuis');
    const diepten = (lijst) => [...new Set(lijst.map((p) => p.plannedDepthCm).filter(Boolean))].sort((a, b) => a - b).join('/');
    const deel = [
      boringen.length && `${meervoud(boringen.length, 'boring', 'boringen')}${diepten(boringen) ? ` tot ${diepten(boringen)} cm` : ''}`,
      pb.length && `${meervoud(pb.length, 'peilbuis', 'peilbuizen')}${diepten(pb) ? ` tot ${diepten(pb)} cm` : ''}`,
    ].filter(Boolean).join(' · ');
    const o = this._opgeslagen?.subIds?.includes(s.id) ? this._opgeslagen : null;
    return html`
      ${o ? html`<p class="gelukt">✓ ${meervoud(o.aantal, 'geplande boring', 'geplande boringen')} ingetekend${o.subIds.length > 1
        ? ` in ${o.subIds.length} deellocaties` : ''}${o.vervangen ? ` (${meervoud(o.vervangen, 'oude', 'oude')} vervangen)` : ''}.
        ${(o.blijven || []).filter((b) => b.aantal).map((b) => `In ${b.naam} ${b.aantal === 1 ? 'is 1 boring' : `zijn ${b.aantal} boringen`} blijven staan (met de hand aangepast). `)}
        Ze staan op de projectkaart en in de veldapp.</p>` : ''}
      <p class="klein">${deel || 'De geplande boringen worden geladen…'}</p>
      <p class="sleeptip">${unsafeSVG(lucideIcon(Move, 14))}<span>Staat een punt verkeerd, bijvoorbeeld op een kabel of in een
        gebouw? Klik op <b>Punten verplaatsen</b>, versleep ze en sla ze in één keer op. Klik op een punt voor de RD-coördinaten.</span></p>
      <div class="acties">
        <button class="knop hoofd" ?disabled=${this._bezig} @click=${() => this._sluitDl(s)}>${this._bezig ? 'Opslaan…' : 'Opslaan'}</button>
        <button class="knop" ?disabled=${this._bewerken} @click=${() => this._startVerplaatsen()}>${unsafeSVG(lucideIcon(Move, 14))} Punten verplaatsen</button>
        <button class="knop" @click=${() => { this._annuleerVerplaatsen(); this._opgeslagen = null; this._naarStapVan(s, 3); }}>Aantallen of diepten aanpassen</button>
      </div>`;
  }

  /**
   * Klaar met deze deellocatie: verschoven punten bewaren (als je aan het verplaatsen was) en
   * de tegel inklappen. Lukt het bewaren niet, dan blijft hij open met de melding.
   */
  async _sluitDl(s) {
    if (this._bewerken) {
      await this._bewaarVerplaatsen();
      if (this._bewerken) return;
    }
    this._opgeslagen = null;
    if (this._dlKeuze === s.id) this._dlKeuze = null;
  }

  static styles = css`${unsafeCSS(base)}
    :host { display:block; }
    /* Kaart en stappen ongeveer 55/45: het stappenpaneel heeft ruimte nodig om rustig te lezen. */
    .vo { display:grid; grid-template-columns:minmax(0,11fr) minmax(400px,9fr); gap:20px; align-items:start; }
    @media (max-width: 1000px) { .vo { grid-template-columns:minmax(0,1fr); } }
    /* Vaste hoogte (zie _pasHoogteAan): de kaart groeit niet mee met het paneel ernaast. */
    .kaartvak { position:relative; height:var(--vo-hoogte, 640px); border:1px solid var(--mv-border); border-radius:12px; overflow:hidden; }
    ${unsafeCSS(leafletCss)}
    .kaart { position:absolute; inset:0; }
    ${unsafeCSS(SUBLOCATION_MAP_CSS)}
    .kaartknoppen { position:absolute; top:10px; right:10px; left:56px; z-index:1001; display:flex; flex-wrap:wrap; justify-content:flex-end;
                    gap:8px; align-items:flex-start; pointer-events:none; }
    .kaartknoppen > * { pointer-events:auto; }
    /* Zelfde maat als de knoppen op de projectkaart (.btn-plain daar). */
    .kaartknoppen .knop { white-space:nowrap; padding:6px 12px; font-size:.76rem; font-weight:600; gap:5px; border-radius:7px; box-shadow:0 1px 4px rgba(0,0,0,.12); }
    .seg { display:flex; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:7px; overflow:hidden; box-shadow:0 1px 4px rgba(0,0,0,.12); }
    .seg button { border:none; background:none; padding:6px 12px; font-size:.76rem; font-weight:600; color:var(--mv-text-muted); cursor:pointer; font-family:inherit; }
    .seg button.on { background:var(--mv-primary); color:#fff; }
    .exportmenu { position:relative; }
    .knop.aan { border-color:var(--mv-primary); color:var(--mv-primary); }
    .telling { font-size:.68rem; font-weight:700; background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); border-radius:999px; padding:0 7px; line-height:1.5; }
    ${unsafeCSS(TEKENINGEN_PANEEL_CSS)}
    .tekeningpaneel { position:absolute; right:0; top:calc(100% + 6px); width:290px; max-height:340px; overflow:auto; padding:8px;
                      display:flex; flex-direction:column; gap:4px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border);
                      border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,.18); }
    .tekeningpaneel .uploaden { justify-content:center; margin-bottom:4px; }
    .geentekening { padding:4px 4px 2px; }
    .tekening { display:flex; align-items:flex-start; gap:8px; padding:6px 4px; border-radius:7px; }
    .tekening:hover { background:var(--mv-surface-alt,#f8fafc); }
    .oog { flex:none; border:none; background:none; padding:2px; cursor:pointer; color:var(--mv-text-sub); display:inline-flex; margin-top:1px; }
    .tekeningtekst { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; cursor:pointer; }
    .tekeningtekst strong { font-size:.8rem; word-break:break-word; }
    .tekeningtekst strong.uit { color:var(--mv-text-muted); }
    .tekeningtekst span { font-size:.7rem; color:var(--mv-text-muted); }
    .dekking { width:100%; max-width:150px; margin-top:4px; accent-color:var(--mv-primary); }
    .weg { flex:none; border:none; background:none; padding:2px; cursor:pointer; color:var(--mv-text-muted); display:inline-flex; border-radius:5px; }
    .weg:hover { color:#b91c1c; background:#fef2f2; }
    .importblok { display:flex; flex-direction:column; gap:5px; padding:10px 12px; border:1.5px dashed #c2410c; border-radius:10px; background:#fff7ed; }
    .importblok .rij { display:flex; gap:8px; }
    .puntblok .rij { display:flex; gap:8px; }
    .puntblok { display:flex; flex-direction:column; gap:6px; padding:10px 12px; border:1.5px solid #2563eb; border-radius:10px; background:#eff6ff; }
    .puntblok .kop { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
    .punttitel { display:flex; flex-direction:column; gap:2px; }
    .puntblok.flits { animation:flits 1.2s ease-out; }
    @keyframes flits { 0% { box-shadow:0 0 0 0 rgba(37,99,235,.55); } 100% { box-shadow:0 0 0 14px rgba(37,99,235,0); } }
    .punttitel b { font-size:.95rem; color:#1d4ed8; }
    .mv-halo { animation:mv-halo 1.6s ease-out infinite; }
    @keyframes mv-halo { 0% { transform:scale(.7); opacity:1; } 100% { transform:scale(1.5); opacity:0; } }
    .puntblok .opgaven input { width:7.5rem; text-align:left; }
    .vo-gepland { display:block; width:12px; height:12px; border-radius:50%; border:2px dashed #2563eb; background:rgba(255,255,255,.85);
                  transform:translate(-50%,-50%); cursor:grab; }
    .vo-gepland.peilbuis { border-radius:3px; }
    .vo-gepland:not(.sleepbaar) { cursor:pointer; border-style:solid; }
    .vo-gepland.sleepbaar { cursor:grab; border-width:2.5px; box-shadow:0 0 0 3px rgba(37,99,235,.18); }
    .vo-gepland.verschoven { background:#2563eb; border-style:solid; }
    ${unsafeCSS(KAART_LEGENDA_CSS)}
    .legend { bottom:22px; }
    .mv-planned.sleepbaar { cursor:grab; }
    .mv-planned.sleepbaar > div > div:first-child { box-shadow:0 0 0 4px rgba(37,99,235,.22), 0 1px 4px rgba(0,0,0,.35) !important; }
    .mv-planned.verschoven > div > div:first-child { box-shadow:0 0 0 4px rgba(37,99,235,.5), 0 1px 4px rgba(0,0,0,.35) !important; }
    .tekenbalk.verplaatsbalk { border-color:#2563eb; right:auto; }
    .tekenbalk.verplaatsbalk button.hoofd { background:#2563eb; color:#fff; }
    .tekenbalk.verplaatsbalk svg { color:#2563eb; }
    .vo-gepland.gekozen { border-style:solid; background:#2563eb; box-shadow:0 0 0 3px rgba(37,99,235,.3); }
    .knop { display:inline-flex; align-items:center; gap:6px; padding:7px 12px; border-radius:8px; border:1px solid var(--mv-border);
            background:var(--mv-surface); color:var(--mv-text); font:inherit; font-size:.8rem; font-weight:700; cursor:pointer; }
    .knop:hover:not(:disabled) { border-color:var(--mv-primary); }
    .knop.hoofd { background:var(--mv-primary); border-color:var(--mv-primary); color:#fff; }
    .knop:disabled { opacity:.5; cursor:default; }
    .tekenbalk { position:absolute; left:56px; right:170px; top:10px; z-index:600; display:flex; align-items:center; flex-wrap:wrap; gap:8px;
                 padding:7px 10px; border:1px solid #c2410c; border-radius:8px; background:#fff; box-shadow:0 2px 8px rgba(0,0,0,.18); font-size:.8rem; }
    .tekenbalk button { border:none; background:var(--mv-bg,#f1f3f5); border-radius:6px; padding:4px 10px; font:inherit; font-size:.75rem; font-weight:700; cursor:pointer; }
    .tekenbalk button.hoofd { background:#c2410c; color:#fff; }
    .tekenbalk button:disabled { opacity:.5; cursor:default; }
    .tekenbalk input { border:1px solid var(--mv-border); border-radius:6px; padding:4px 8px; font:inherit; width:9rem; }
    .hint { position:absolute; bottom:10px; left:10px; z-index:500; background:#fff; padding:5px 10px; border-radius:7px;
            font-size:.75rem; box-shadow:0 1px 4px rgba(0,0,0,.18); }
    .hint.sleep { display:flex; align-items:center; gap:7px; border:1.5px solid #15803d; background:#f0fdf4; color:#14532d; font-size:.8rem; padding:7px 11px; }
    /* Conceptpunten: gestippeld en met een sleepcursor -- een voorstel dat je nog verplaatst. */
    .vo-punt { display:block; width:14px; height:14px; border-radius:50%; background:rgba(22,163,74,.18); border:2px dashed #15803d;
               transform:translate(-50%,-50%); cursor:grab; box-sizing:border-box; }
    .vo-punt:active { cursor:grabbing; }
    .vo-punt.peilbuis { background:rgba(37,99,235,.18); border-color:#1d4ed8; border-radius:3px; }
    .vo-naam { position:absolute; left:9px; top:0; transform:translateY(-50%); padding:0 4px; border-radius:4px; background:#fff;
               border:1px solid #cddccb; font:800 .62rem/1.5 system-ui,sans-serif; white-space:nowrap; color:#14532d; }

    .paneel { display:flex; flex-direction:column; gap:12px; max-height:var(--vo-hoogte, 640px); overflow:auto; }
    .voortgang { display:flex; flex-direction:column; gap:8px; margin-bottom:4px; }
    .voortgang-kop { display:flex; justify-content:space-between; align-items:baseline; }
    .voortgang-kop span { font-size:.74rem; color:var(--mv-text-muted); }
    .balk { height:4px; border-radius:2px; background:var(--mv-border); overflow:hidden; }
    .balk div { height:100%; background:var(--mv-primary); transition:width .25s; }
    .stap { border:1px solid var(--mv-border); border-radius:10px; background:var(--mv-surface); }
    .stap.open { border-color:var(--mv-primary); }
    .stap header { display:flex; align-items:center; gap:12px; padding:15px 18px; cursor:pointer; }
    .stap.open header { cursor:default; }
    .stap.dicht header { cursor:default; opacity:.55; }
    .nummer { flex:none; width:26px; height:26px; border-radius:50%; border:1.5px solid var(--mv-border); display:flex; align-items:center;
              justify-content:center; font-size:.74rem; font-weight:800; color:var(--mv-text-muted); }
    .stap.open .nummer { border-color:var(--mv-primary); color:var(--mv-primary); }
    .stap.af .nummer { background:var(--mv-primary); border-color:var(--mv-primary); color:#fff; }
    .stapkop { display:flex; flex-direction:column; flex:1; min-width:0; }
    .stapkop { gap:2px; }
    .uitlegtegel { flex:none; display:inline-flex; align-items:center; gap:6px; align-self:flex-start; padding:6px 11px;
                   border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); font:inherit;
                   font-size:.78rem; font-weight:700; color:var(--mv-text); cursor:pointer; }
    .uitlegtegel:hover { border-color:var(--mv-primary); color:var(--mv-primary-hover); }
    .stapkop strong { font-size:.9rem; }
    .stapkop span { font-size:.76rem; color:var(--mv-text-muted); }
    .stapinhoud { display:flex; flex-direction:column; gap:12px; padding:2px 18px 18px 56px; }
    .lijst { display:flex; flex-direction:column; border:1px solid var(--mv-border); border-radius:8px; overflow:hidden; }
    .lijstregel { display:flex; align-items:center; gap:10px; padding:10px 14px; font-size:.82rem; }
    .lijstregel + .lijstregel { border-top:1px solid var(--mv-border); }
    .lijstregel .tekst { margin-left:auto; }
    .lijstregel.samenvatting { flex-direction:column; align-items:stretch; gap:2px; }
    .lijstregel .code { margin-left:auto; font-size:.72rem; font-weight:800; color:var(--mv-text-sub); }
    .dl { color:#c2410c; }
    .dl-ico { width:10px; height:10px; border:1.5px dashed #c2410c; border-radius:2px; background:rgba(194,65,12,.12); flex:none; }
    .dlkop { display:flex; align-items:baseline; gap:8px; }
    .dlkop .klein { margin-left:auto; }
    .chips { display:flex; flex-wrap:wrap; gap:6px; }
    .dlchip { border:1px solid var(--mv-border); border-radius:999px; background:var(--mv-surface); padding:3px 10px; font:inherit;
              font-size:.76rem; font-weight:700; cursor:pointer; color:var(--mv-text); }
    .dlchip small { font-weight:600; color:var(--mv-text-muted); }
    .dlchip.gezet small { color:var(--mv-primary); }
    .dlchip.aan { border-color:var(--mv-primary); background:var(--mv-primary-tint); }
    .dlchip:disabled { cursor:default; color:var(--mv-text-muted); border-style:dashed; background:none; }
    .opdracht { display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
    .aantallen + .aantallen { border-top:1px dashed var(--mv-border); padding-top:9px; }
    .meedoen { display:inline-flex; align-items:center; gap:7px; cursor:pointer; }
    .meedoen input { accent-color:var(--mv-primary); width:15px; height:15px; margin:0; }
    .aantallen.nietmee .dl { opacity:.6; }
    .sleeptip { display:flex; gap:8px; align-items:flex-start; margin:0; padding:8px 10px; border-radius:8px; background:#f0fdf4;
                border:1px solid #bbf7d0; font-size:.76rem; color:#14532d; line-height:1.45; }
    .sleeptip svg { flex:none; margin-top:1px; }
    .popup-achtergrond { position:fixed; inset:0; z-index:2000; background:rgba(15,23,42,.45); display:flex; align-items:center;
                         justify-content:center; padding:24px; }
    .popup { width:min(520px, 100%); max-height:calc(100vh - 48px); display:flex; flex-direction:column; background:var(--mv-surface,#fff);
             border-radius:16px; box-shadow:0 24px 60px rgba(0,0,0,.28); overflow:hidden; }
    .popup-kop { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding:20px 20px 0; }
    .popup-kop h3 { margin:0 0 4px; font-size:1.05rem; font-weight:800; }
    .popup-kop p { margin:0; font-size:.8rem; line-height:1.45; color:var(--mv-text-sub); }
    .sluit { flex:none; width:28px; height:28px; border-radius:7px; border:1px solid var(--mv-border); background:var(--mv-surface-alt,#f8fafc);
             color:var(--mv-text-sub); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; }
    .sluit:hover { color:var(--mv-text); }
    .popup-inhoud { padding:16px 20px 4px; overflow:auto; display:flex; flex-direction:column; gap:14px; }
    .sleepvlak { display:flex; flex-direction:column; align-items:center; gap:4px; padding:22px 16px; border:1.5px dashed var(--mv-border);
                 border-radius:10px; cursor:pointer; text-align:center; transition:border-color .15s, background .15s; }
    .sleepvlak:hover, .sleepvlak:focus-visible { border-color:var(--mv-primary); outline:none; }
    .sleepvlak.erboven { border-color:var(--mv-primary); background:var(--mv-primary-tint,#e9f5e6); }
    .sleepvlak .pijl { width:28px; height:28px; border-radius:50%; background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary);
                       display:inline-flex; align-items:center; justify-content:center; margin-bottom:6px; }
    .sleepvlak strong { font-size:.84rem; color:var(--mv-text); }
    .link { border:none; background:none; padding:0; font:inherit; color:var(--mv-primary); font-weight:600; cursor:pointer; }
    .link:hover { text-decoration:underline; }
    .sectielabel { font-size:.64rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:var(--mv-text-muted);
                   padding-bottom:6px; border-bottom:1px solid var(--mv-border); }
    .soorten { display:flex; flex-direction:column; }
    .soort { display:grid; grid-template-columns:32px minmax(0,1fr) auto; gap:10px; align-items:start; padding:12px 0; }
    .soort + .soort { border-top:1px solid var(--mv-border); }
    .soortlabel { width:32px; height:32px; border-radius:7px; display:inline-flex; align-items:center; justify-content:center;
                  font-size:.5rem; font-weight:800; letter-spacing:.02em; }
    .soortlabel.groen { background:#e9f5e6; color:#2e7d32; }
    .soortlabel.blauw { background:#e8eefc; color:#3b5bdb; }
    .soortlabel.oranje { background:#fdf0e6; color:#c2410c; }
    .soortlabel.paars { background:#f1ecfe; color:#6d28d9; }
    .exportkeuzes { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
    .exportkeuzes label { display:flex; flex-direction:column; gap:4px; font-size:.7rem; font-weight:700; color:var(--mv-text-sub); }
    .exportkeuzes select { padding:7px 9px; border:1px solid var(--mv-border); border-radius:8px; font:inherit; font-size:.82rem; font-weight:600;
                           background:var(--mv-surface,#fff); color:var(--mv-text); }
    .formaattegels { display:grid; grid-template-columns:1fr 1fr; gap:10px; padding-bottom:8px; }
    .formaattegel { display:grid; grid-template-columns:32px minmax(0,1fr) auto; gap:10px; align-items:start; text-align:left;
                    padding:12px; border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface,#fff);
                    font:inherit; color:var(--mv-text); cursor:pointer; transition:border-color .15s, background .15s; }
    .formaattegel:hover, .formaattegel:focus-visible { border-color:var(--mv-primary); background:var(--mv-primary-tint,#e9f5e6); outline:none; }
    .tegeltekst { display:flex; flex-direction:column; gap:2px; min-width:0; }
    .tegeltekst strong { font-size:.84rem; }
    .tegeltekst > span { font-size:.74rem; color:var(--mv-text-sub); line-height:1.4; }
    .tegeldownload { color:var(--mv-text-muted); margin-top:2px; }
    .formaattegel:hover .tegeldownload { color:var(--mv-primary); }
    @media (max-width: 560px) { .formaattegels, .exportkeuzes { grid-template-columns:1fr; } }
    .soorttekst { display:flex; flex-direction:column; gap:2px; min-width:0; }
    .soorttekst strong { font-size:.82rem; }
    .soorttekst > span { font-size:.76rem; color:var(--mv-text-sub); line-height:1.4; }
    .ext { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:.68rem; color:var(--mv-text-muted); background:none; padding:0; }
    .kolomknop { align-self:flex-start; font-size:.74rem; margin-top:6px; }
    .wordt { white-space:nowrap; font-size:.68rem; font-weight:600; color:var(--mv-text-sub); background:var(--mv-surface-alt,#f1f5f9);
             border-radius:999px; padding:3px 9px; margin-top:2px; }
    .kolomtabel { grid-column:2 / 4; margin-top:-2px; border:1px solid var(--mv-border); border-radius:8px; overflow:hidden; }
    .kolomrij { display:grid; grid-template-columns:80px minmax(0,1fr) auto; gap:10px; align-items:baseline; padding:6px 10px; font-size:.72rem; }
    .kolomrij + .kolomrij { border-top:1px solid var(--mv-border); }
    .kolomrij code { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-weight:700; font-size:.7rem; color:var(--mv-text); }
    .kolomrij code.vb { font-weight:400; color:var(--mv-text-muted); text-align:right; }
    .kolomrij span { color:var(--mv-text-sub); }
    .kolomnoot { padding:7px 10px; border-top:1px solid var(--mv-border); background:var(--mv-surface-alt,#f8fafc); font-size:.7rem; color:var(--mv-text-sub); }
    .popup-voet .klein { white-space:nowrap; }
    .popup-voet { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:12px 20px; margin-top:8px;
                  border-top:1px solid var(--mv-border); }
    .gelukt { margin:0; font-size:.8rem; color:var(--mv-primary-hover); font-weight:600; line-height:1.45; }
    h3 { margin:0; font-size:1rem; }
    .uitleg, .klein { margin:0; font-size:.76rem; color:var(--mv-text-muted); line-height:1.45; }
    .melding { margin:0; padding:8px 10px; border-radius:8px; background:var(--mv-primary-tint); font-size:.78rem; }
    .leeg { display:flex; gap:10px; align-items:center; padding:16px; border:1.5px dashed var(--mv-border); border-radius:10px;
            font-size:.8rem; color:var(--mv-text-sub); }
    .vlak { border:1px solid var(--mv-border); border-radius:10px; padding:12px; display:flex; flex-direction:column; gap:8px; background:var(--mv-surface); }
    .vlak.actief { border-color:var(--mv-primary); }
    .vlak header { display:flex; align-items:baseline; gap:8px; }
    .vlak header strong { color:#c2410c; }
    .maat { font-size:.74rem; color:var(--mv-text-muted); margin-left:auto; }
    .veld { display:flex; flex-direction:column; gap:4px; font-size:.72rem; font-weight:700; color:var(--mv-text-sub); }
    .veld select { padding:6px 8px; border-radius:7px; border:1px solid var(--mv-border); font:inherit; font-size:.8rem; }
    .veld input { padding:7px 9px; border-radius:7px; border:1px solid var(--mv-border); font:inherit; font-size:.82rem; font-weight:400;
                  color:var(--mv-text); background:var(--mv-surface); }
    .veld input:focus { outline:none; border-color:var(--mv-primary); }
    .veld .optie { font-weight:500; color:var(--mv-text-muted); }
    .veld .klein .tekst { margin-left:4px; }
    .dlvelden { display:flex; flex-direction:column; gap:10px; }
    .waarschuwing { margin:0; padding:6px 8px; border-radius:7px; background:#fff7ed; color:#9a3412; font-size:.74rem; }
    .norm { margin:0; font-size:.76rem; font-weight:700; color:var(--mv-primary-hover); }
    .normblok { display:flex; flex-direction:column; gap:3px; padding:7px 9px; border-radius:8px; background:var(--mv-primary-tint); }
    .notitie { margin:0; font-size:.72rem; color:#9a3412; }
    .opgaven { display:grid; grid-template-columns:1fr 1fr; gap:6px 10px; }
    .opgaven label { display:flex; flex-direction:column; gap:2px; font-size:.7rem; font-weight:700; color:var(--mv-text-sub); }
    .opgaven span { display:flex; align-items:center; gap:4px; font-weight:600; }
    .opgaven input { width:4.5rem; padding:4px 6px; border:1px solid var(--mv-border); border-radius:6px; font:inherit; text-align:right; }
    .aantallen { display:flex; flex-direction:column; gap:6px; }
    .rij { display:flex; align-items:center; gap:6px; font-size:.78rem; flex-wrap:wrap; }
    .rij input { width:4.5rem; padding:4px 6px; border:1px solid var(--mv-border); border-radius:6px; font:inherit; text-align:right; }
    .tekst { border:none; background:none; font:inherit; font-size:.76rem; font-weight:800; color:var(--mv-text-sub); cursor:pointer; padding:0; }
    .tekst.groen { color:var(--mv-primary); align-self:flex-start; }
    .acties { display:flex; gap:8px; flex-wrap:wrap; }
    .concept { display:flex; flex-direction:column; gap:8px; border-top:1px dashed var(--mv-border); padding-top:8px; }
    .conceptlijst { display:flex; flex-wrap:wrap; gap:4px; }
    .chip { font-size:.7rem; font-weight:800; padding:2px 7px; border-radius:6px; background:#dcfce7; color:#14532d; }
    .chip.peilbuis { background:#dbeafe; color:#1e3a8a; }
    .chip small { font-weight:600; opacity:.75; }
  
    /* Tegels per deellocatie */
    .tegels { display:flex; flex-direction:column; gap:10px; }
    .tegel { border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface); overflow:hidden; scroll-margin-top:12px; }
    .tegel.open { border-color:#c2410c; box-shadow:0 0 0 1px #c2410c33; }
    .tegel > header { display:flex; align-items:center; gap:10px; padding:11px 14px; cursor:pointer; }
    .tegel > header:hover { background:var(--mv-surface-alt); }
    .tegelkop { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
    .tegelkop > div { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
    .tegelstand { font-size:.72rem; font-weight:700; color:var(--mv-text-sub); background:var(--mv-surface-alt); border-radius:999px; padding:2px 8px; }
    .tegelstand.af { color:var(--mv-primary); background:var(--mv-primary-tint); }
    .tegelpijl { color:var(--mv-text-muted); font-size:.8rem; width:12px; }
    .tegelinhoud { border-top:1px solid var(--mv-border-soft, var(--mv-border)); padding:10px 12px 12px; display:flex; flex-direction:column; gap:8px; }
    .tegelinhoud .stap { margin:0; }
    .overlapchip { font-size:.66rem; font-weight:700; color:var(--mv-warning-text); background:var(--mv-warning-panel-bg); border-radius:999px; padding:1px 7px; }
    .overlapmelding, .overlapkeuze { background:var(--mv-warning-panel-bg); color:var(--mv-warning-text); border-radius:9px; padding:9px 12px;
      font-size:.78rem; line-height:1.5; display:flex; flex-direction:column; gap:4px; }
    .overlapkeuze b, .overlapmelding b { color:inherit; }
    .overlapkeuze .acties { margin-top:6px; flex-wrap:wrap; }
    .nieuwdl { width:100%; margin-top:10px; display:flex; align-items:center; justify-content:center; gap:8px; padding:12px 14px;
      border:1.5px dashed var(--mv-primary); border-radius:12px; background:var(--mv-primary-light); color:var(--mv-primary);
      font:inherit; font-size:.875rem; font-weight:700; cursor:pointer; transition:background .15s; }
    .nieuwdl:hover:not(:disabled) { background:var(--mv-primary-tint); }
    .nieuwdl:disabled { opacity:.5; cursor:default; }
`;
}

customElements.define('vooronderzoek-panel', VooronderzoekPanel);
